<template>
  <div class="about-page">
    <TasterPack />

    <main id="main-content">
    <section id="header">
      <!--
        Hero <video> with src set via JS post-mount — see mounted()
        below. We previously used <source media="…"> children to let
        the browser pick the right variant per viewport, but Lighthouse
        confirmed that approach still downloads BOTH sources on mobile
        (~10 MB transferred for a hero loop that only needed 3.4 MB).
        With no <source> children in the HTML the browser has nothing
        to fetch until JS picks one.

        Poster is painted as a CSS background-image on #header with a
        media query (see scoped styles) so desktop gets the landscape
        1280×720 poster and mobile gets the portrait 720×1280 — no
        stretched/zoomed crop from a single poster being resized to a
        mismatched aspect ratio. The video sits transparent above it
        and paints over the poster once the mp4 begins playback.
      -->
      <video
        ref="heroVideo"
        class="hero-video"
        aria-hidden="true"
        autoplay
        loop
        muted
        playsinline
        preload="none"
        @play="heroVideoPaused = false"
        @pause="heroVideoPaused = true"
      ></video>
      <div class="header-overlay">
        <span class="hero-label" aria-hidden="true">
          <span class="hero-label-rule"></span>
          <span>GYMPODS<sup class="reg-mark">&reg;</sup>&ensp;&middot;&ensp;Private Gym PODs</span>
        </span>
        <h1>
          <!--
            Visually-hidden keyword-led prefix for SEO & screen readers.
            Sighted users see the two-line statement below; each line
            sits in its own mask so it can slide up on entrance.
          -->
          <span class="sr-only">Private Gym PODs in Dalston &amp; Putney &mdash; </span>
          <span class="h1-main">
            <span class="h1-line"><span>Redefining</span></span>
            <span class="h1-line h1-line--accent"><span>personal fitness.</span></span>
          </span>
        </h1>
        <p class="hero-tagline">Your own private gym. No waiting, no crowds, <span style="white-space:nowrap">no gym-timidation.</span></p>
        <div class="hero-bottom">
          <router-link :to="bookPodTo" class="cta-button" data-cta="book-pod">Book a POD <ArrowRight :size="14" /></router-link>
          <div class="hero-apps">
            <a
              href="https://apps.apple.com/gb/app/gympods/id6502242401"
              target="_blank"
              rel="noopener noreferrer"
              class="hero-app-btn"
              aria-label="Download GYMPODS on the App Store (opens in new tab)"
            >
              <IconApple :size="18" aria-hidden="true" />
              <span class="hero-app-btn-text">
                <span class="hero-app-btn-eyebrow">Download</span>
                <span class="hero-app-btn-name">App Store</span>
              </span>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.gympods.app"
              target="_blank"
              rel="noopener noreferrer"
              class="hero-app-btn"
              aria-label="Get GYMPODS on Google Play (opens in new tab)"
            >
              <IconGooglePlay :size="18" aria-hidden="true" />
              <span class="hero-app-btn-text">
                <span class="hero-app-btn-eyebrow">Download</span>
                <span class="hero-app-btn-name">Google Play</span>
              </span>
            </a>
          </div>
        </div>
      </div>
      <div class="hero-controls">
        <!-- Pause control for the looping background video (WCAG 2.2.2). -->
        <button
          type="button"
          class="hero-video-toggle"
          :aria-label="heroVideoPaused ? 'Play background video' : 'Pause background video'"
          :aria-pressed="heroVideoPaused ? 'true' : 'false'"
          @click="toggleHeroVideo"
        >
          <Play v-if="heroVideoPaused" :size="14" aria-hidden="true" />
          <Pause v-else :size="14" aria-hidden="true" />
        </button>
        <a href="#locations" class="hero-scroll" aria-label="Scroll to our locations">
          <span class="hero-scroll-text" aria-hidden="true">Scroll</span>
          <span class="hero-scroll-line" aria-hidden="true"></span>
        </a>
      </div>
    </section>

    <section id="locations">
      <header class="section-header">
        <span class="section-eyebrow">Our Locations</span>
        <h2>Two London spaces.<br />One GYMPODS experience.</h2>
      </header>
      <div class="locations-grid">
        <article
          v-for="loc in heroStats"
          :key="loc.key"
          class="location-card"
          :class="`location-card--${loc.key}`"
        >
          <span class="location-accent" aria-hidden="true"></span>
          <span v-if="loc.key === 'putney'" class="location-new-badge" aria-label="New location">New</span>
          <header class="location-card-head">
            <span class="location-prefix">GYMPODS</span>
            <h3 class="location-city">{{ loc.name }}</h3>
          </header>
          <ul class="location-stats">
            <li v-for="p in loc.platforms" :key="p.name">
              <component
                :is="p.url ? 'a' : 'div'"
                :href="p.url || null"
                :target="p.url ? '_blank' : null"
                :rel="p.url ? 'noopener noreferrer' : null"
                :aria-label="p.url ? `View GYMPODS ${loc.name} reviews on ${p.name} (opens in new tab)` : null"
                class="location-stat-row"
                :class="{ 'location-stat-row--link': !!p.url }"
              >
                <span class="location-platform">{{ p.name }}</span>
                <span class="location-rating">
                  <Star :size="14" aria-hidden="true" />
                  <strong>{{ p.rating }}</strong>
                </span>
                <span class="location-count">{{ p.countLabel }}</span>
              </component>
            </li>
          </ul>
          <router-link
            :to="`/${loc.key}`"
            class="location-cta"
            :aria-label="`Explore GYMPODS ${loc.name}`"
          >
            Explore {{ loc.name }}
            <ArrowRight :size="14" aria-hidden="true" />
          </router-link>
        </article>
      </div>
      <!-- Below the location cards, under the full-height hero, so it moves nothing above the fold when it appears. -->
      <FreePtPromo medium="homepage" class="home-free-pt-promo" />
    </section>

    <hr class="section-divider" />

    <section id="intro">
      <header class="section-header">
        <span class="section-eyebrow">Why GYMPODS</span>
        <h2>Award-winning<br />private fitness spaces.</h2>
      </header>
      <AsSeenIn />
      <p class="section-lead intro-lead">GYMPODS gives you your own private gym space &mdash; spacious, fully equipped, and ready when you are.<br /><strong>No queues. No sharing. No compromise.</strong></p>

      <div id="features">
        <article class="why-tile" tabindex="0">
          <div class="feature-circle"><Shield :size="16" /></div>
          <div class="why-tile-body">
            <h3>Safe &amp; Secure</h3>
            <p class="why-tile-desc">Secure entry, and help on hand at every hour.</p>
            <p class="why-tile-proof">Our team is on site for almost every session, to help with check-in, equipment and anything you need, and our 24/7 AI assistant is one tap away on WhatsApp.</p>
          </div>
        </article>
        <article class="why-tile" tabindex="0">
          <div class="feature-circle"><SprayCan :size="16" /></div>
          <div class="why-tile-body">
            <h3>Clean</h3>
            <p class="why-tile-desc">Regular cleans and checks throughout the day.</p>
            <p class="why-tile-proof">PODs are reset, restocked and checked through the day, with our team on hand to handle anything that needs attention.</p>
          </div>
        </article>
        <article class="why-tile" tabindex="0">
          <div class="feature-circle"><Maximize2 :size="16" /></div>
          <div class="why-tile-body">
            <h3>Spacious</h3>
            <p class="why-tile-desc">Room to move, stretch and train properly.</p>
            <p class="why-tile-proof">Our PODs are large enough for full barbell work and training with a buddy &mdash; not converted cupboards.</p>
          </div>
        </article>
        <article class="why-tile" tabindex="0">
          <div class="feature-circle"><Dumbbell :size="16" /></div>
          <div class="why-tile-body">
            <h3>Fully Equipped</h3>
            <p class="why-tile-desc">Serious kit, ready when you are.</p>
            <p class="why-tile-proof">Squat racks, cables, leg machines, Peloton bikes, StairMasters and more &mdash; every POD is kitted out for proper training.</p>
          </div>
        </article>
      </div>

      <aside class="why-compare">
        <p class="why-compare-eyebrow">The GYMPODS Difference</p>
        <p class="why-compare-body">GYMPODS is built around service: a real team on site for almost every session, plus a 24/7 AI assistant on WhatsApp whenever you need help.</p>
      </aside>
    </section>

    <hr class="section-divider" />

    <section id="train-how">
      <header class="section-header">
        <span class="section-eyebrow">How you train</span>
        <h2>Train how you like.</h2>
      </header>
      <div id="icons" class="photo-cards">
        <div class="photo-card">
          <div class="photo-card-img">
            <img loading="lazy" decoding="async" src="../assets/dalston-train-solo.webp" alt="Solo training session inside a private GYMPODS pod" width="1000" height="1500" />
          </div>
          <div class="photo-card-overlay">
            <span class="photo-card-accent"></span>
            <p class="photo-card-title">Train solo</p>
            <p class="photo-card-sub">Your space, your workout</p>
          </div>
        </div>
        <div class="photo-card">
          <div class="photo-card-img">
            <img loading="lazy" decoding="async" src="../assets/dalston-with-a-buddy.webp" alt="Two people training together in a private GYMPODS pod" width="1000" height="1500" />
          </div>
          <div class="photo-card-overlay">
            <span class="photo-card-accent"></span>
            <p class="photo-card-title">With a buddy</p>
            <p class="photo-card-sub">Bring a friend, double the fun</p>
          </div>
        </div>
        <div class="photo-card">
          <div class="photo-card-img">
            <img loading="lazy" decoding="async" src="../assets/dalston-pt-with-clients.webp" alt="Personal trainer coaching a client in a private GYMPODS pod" width="1000" height="1500" />
          </div>
          <div class="photo-card-overlay">
            <span class="photo-card-accent"></span>
            <p class="photo-card-title">Personal Training</p>
            <p class="photo-card-sub">Your own private training base</p>
          </div>
        </div>
      </div>
    </section>

    <hr class="section-divider" />

    <section id="pods">
      <header class="section-header">
        <span class="section-eyebrow">The PODs</span>
        <h2>Choose your POD.</h2>
      </header>
      <p class="section-lead">With a range of POD types available, you have all the equipment you need to reach your personal goals, mix it up and train when and how you want to.</p>
      <div class="pod-grid">
        <div class="pod-card">
          <div class="pod-image-wrap">
            <!--
              <picture>: browser picks the webp variant if supported (every
              evergreen browser since 2020), falls back to the JPEG for
              older UAs. 800/1600 covers phone & retina-desktop crops; the
              sizes hint tells the browser the image renders at ~50vw on
              the 2-col desktop grid, 100vw on the single-col mobile stack.
            -->
            <picture>
              <source type="image/avif" srcset="../assets/strength-putney-800.avif 800w, ../assets/strength-putney-1600.avif 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <source type="image/webp" srcset="../assets/strength-putney-800.webp 800w, ../assets/strength-putney-1600.webp 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <img loading="lazy" decoding="async" src="../assets/strength-putney.jpg" alt="Strength POD at GYMPODS with half rack and dumbbells" width="2000" height="1600" />
            </picture>
            <span class="pod-badge"><Coins :size="12" aria-hidden="true" />60 mins = 3 credits</span>
          </div>
          <div class="pod-body">
            <div class="pod-info">
              <h3>Strength POD</h3>
              <p>Your own private space to build strength.</p>
            </div>
            <div class="perfect-for">
              <p><strong>Perfect for:</strong></p>
              <ul>
                <li><span>&#x2B24;</span> Upper and lower body strength</li>
                <li><span>&#x2B24;</span> Muscle building</li>
                <li><span>&#x2B24;</span> Cable &amp; isolation exercises</li>
                <li><span>&#x2B24;</span> Squats and deadlifts</li>
              </ul>
            </div>
            <div class="equipment-strip">
              <div class="equip-item"><Move :size="16" /><span>Half Rack</span></div>
              <div class="equip-item"><ArrowDownUp :size="16" /><span>Dual Pulley</span></div>
              <div class="equip-item"><Dumbbell :size="16" /><span>Dumbbells</span></div>
              <div class="equip-item"><ChevronsUp :size="16" /><span>Leg Press</span></div>
              <div class="equip-item"><Bed :size="16" /><span>Bench</span></div>
              <div class="pod-location-links">
                <router-link to="/putney#pods" class="pod-location-link">See Putney <ArrowRight :size="14" aria-hidden="true" /></router-link>
                <router-link to="/dalston#pods" class="pod-location-link">See Dalston <ArrowRight :size="14" aria-hidden="true" /></router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="pod-card">
          <div class="pod-image-wrap">
            <picture>
              <source type="image/avif" srcset="../assets/putney-power-4-800.avif 800w, ../assets/putney-power-4-1600.avif 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <source type="image/webp" srcset="../assets/putney-power-4-800.webp 800w, ../assets/putney-power-4-1600.webp 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <img loading="lazy" decoding="async" src="../assets/putney-power-4.jpg" alt="Power POD at GYMPODS with Smith machine, dumbbell rack, bench and punch bag" width="2000" height="1333" />
            </picture>
            <span class="pod-badge"><Coins :size="12" aria-hidden="true" />60 mins = 3 credits</span>
          </div>
          <div class="pod-body">
            <div class="pod-info">
              <h3>Power POD</h3>
              <p>Designed for high-energy sessions.</p>
            </div>
            <div class="perfect-for">
              <p><strong>Perfect for:</strong></p>
              <ul>
                <li><span>&#x2B24;</span> HIIT and circuit training</li>
                <li><span>&#x2B24;</span> Full body toning</li>
                <li><span>&#x2B24;</span> Boxing &amp; bag work</li>
                <li><span>&#x2B24;</span> Guided squats on the Smith machine</li>
              </ul>
            </div>
            <div class="equipment-strip">
              <div class="equip-item"><Dumbbell :size="16" /><span>Smith Machine</span></div>
              <div class="equip-item"><PersonStanding :size="16" /><span>SkiErg</span></div>
              <div class="equip-item"><Tv :size="16" /><span>Smart Fitness Mirror</span></div>
              <div class="equip-item"><Hand :size="16" /><span>Punch Bag</span></div>
              <div class="equip-item"><ChevronsUp :size="16" /><span>Leg Press</span></div>
              <div class="pod-location-links">
                <router-link to="/putney#pods" class="pod-location-link">See Putney <ArrowRight :size="14" aria-hidden="true" /></router-link>
                <router-link to="/dalston#pods" class="pod-location-link">See Dalston <ArrowRight :size="14" aria-hidden="true" /></router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="pod-card">
          <div class="pod-image-wrap">
            <picture>
              <source type="image/avif" srcset="../assets/holistic-putney-800.avif 800w, ../assets/holistic-putney-1600.avif 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <source type="image/webp" srcset="../assets/holistic-putney-800.webp 800w, ../assets/holistic-putney-1600.webp 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <img loading="lazy" decoding="async" src="../assets/holistic-putney.jpg" alt="Pilates POD at GYMPODS Putney with Balanced Body Allegro 2 reformer" width="2000" height="1600" />
            </picture>
            <span class="pod-badge"><Coins :size="12" aria-hidden="true" />60 mins = 3 credits</span>
          </div>
          <div class="pod-body">
            <div class="pod-info">
              <h3>Pilates POD</h3>
              <p>Your own private Pilates studio. The only reformer Pilates gym in Putney where you get the entire room to yourself.</p>
            </div>
            <div class="perfect-for">
              <p><strong>Perfect for:</strong></p>
              <ul>
                <li><span>&#x2B24;</span> Reformer Pilates sessions</li>
                <li><span>&#x2B24;</span> Core strength</li>
                <li><span>&#x2B24;</span> Mobility &amp; stretching</li>
                <li><span>&#x2B24;</span> Yoga &amp; floor work</li>
              </ul>
            </div>
            <div class="equipment-strip">
              <div class="equip-item"><Flower :size="16" /><span>Reformer</span></div>
              <div class="equip-item"><Tv :size="16" /><span>Pilates Classes</span></div>
              <div class="equip-item"><Monitor :size="16" /><span>Touchscreen</span></div>
              <div class="equip-item"><Thermometer :size="16" /><span>Temp Control</span></div>
              <div class="pod-location-links">
                <router-link to="/putney#pods" class="pod-location-link">See Putney <ArrowRight :size="14" aria-hidden="true" /></router-link>
                <router-link to="/reformer-pilates-putney" class="pod-location-link">Reformer Pilates at Putney <ArrowRight :size="14" aria-hidden="true" /></router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="pod-card">
          <div class="pod-image-wrap">
            <picture>
              <source type="image/avif" srcset="../assets/cardio-putney-800.avif 800w, ../assets/cardio-putney-1600.avif 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <source type="image/webp" srcset="../assets/cardio-putney-800.webp 800w, ../assets/cardio-putney-1600.webp 1600w" sizes="(min-width: 925px) 50vw, 100vw" />
              <img loading="lazy" decoding="async" src="../assets/cardio-putney.jpg" alt="Cardio POD at GYMPODS with Peloton Bike+" width="2000" height="1600" />
            </picture>
            <span class="pod-badge"><Coins :size="12" aria-hidden="true" />30 mins = 1 credit</span>
          </div>
          <div class="pod-body">
            <div class="pod-info">
              <h3>Cardio POD</h3>
              <p>Your own private cardio studio — treadmill, bike, or StairMaster, each in its own <span style="white-space:nowrap">dedicated POD.</span></p>
            </div>
            <div class="perfect-for">
              <p><strong>Perfect for:</strong></p>
              <ul>
                <li><span>&#x2B24;</span> Cardio &amp; fat loss</li>
                <li><span>&#x2B24;</span> Building endurance</li>
                <li><span>&#x2B24;</span> Warm-ups &amp; cool-downs</li>
                <li><span>&#x2B24;</span> Glutes, quads, hamstrings and calves</li>
              </ul>
            </div>
            <p class="cardio-note">Each Cardio POD contains one of the following:</p>
            <div class="equipment-strip cardio-options">
              <div class="equip-item"><Bike :size="16" /><span>Peloton Bike+</span></div>
              <span class="equip-or">or</span>
              <div class="equip-item"><PersonStanding :size="16" /><span>Treadmill</span></div>
              <span class="equip-or">or</span>
              <div class="equip-item"><TrendingUp :size="16" /><span>StairMaster</span></div>
              <div class="pod-location-links">
                <router-link to="/putney#pods" class="pod-location-link">See Putney <ArrowRight :size="14" aria-hidden="true" /></router-link>
                <router-link to="/dalston#pods" class="pod-location-link">See Dalston <ArrowRight :size="14" aria-hidden="true" /></router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <hr class="section-divider" />

    <section id="pricing">
      <header class="section-header">
        <span class="section-eyebrow">Pricing</span>
        <h2>Pricing at a glance.</h2>
      </header>
      <p class="section-lead">GYMPODS is a pay as you go gym. Use <router-link to="/pricing">credits to book a POD</router-link>.<br />Or choose a monthly credits bundle for better value. <strong>Cancel anytime.</strong></p>

      <div id="selector">
        <button @click="showPricing = 'payg'" :class="showPricing == 'payg' ? 'active' : ''">Pay as you go</button>
        <button @click="showPricing = 'monthly'" :class="showPricing == 'monthly' ? 'active' : ''">Monthly</button>
      </div>

      <!--
        Both tile groups are kept in the DOM and toggled via v-show
        rather than v-if. The previous <transition mode="out-in"> + v-if
        destroyed and re-mounted ~12 elements each click — the slow
        click handler was a likely INP contributor (field INP 247 ms).
        v-show toggles `display: none`, no DOM destroy/create, the
        click handler is fast. The fade was visual polish; we keep a
        very short fade in CSS instead so the tab switch still feels
        considered.
      -->
      <div id="tiles" v-show="showPricing == 'payg'">
        <div class="tile">
          <div class="tile-accent"></div>
          <div class="tile-content">
            <div class="tile-header">
              <span class="tile-name">Cardio POD</span>
            </div>
            <div class="tile-credit-block">
              <Coins :size="28" class="tile-credit-coin" aria-hidden="true" />
              <span class="credit-hero">
                <span class="credit-number">1</span>
                <span class="credit-unit">credit</span>
              </span>
              <span class="credit-detail">per session</span>
              <span class="credit-detail">30 minutes</span>
            </div>
            <router-link to="/pricing#payg" class="tile-secondary-cta">Buy Credits <ArrowRight :size="14" /></router-link>
            <button data-cta="book-pod" @click="this.$router.push('/checkout')" aria-label="Book a Cardio POD">Book a Cardio POD <ArrowRight :size="14" /></button>
          </div>
        </div>

        <div class="tile">
          <div class="tile-accent"></div>
          <div class="tile-content">
            <div class="tile-header">
              <span class="tile-name">Strength, Power &amp; Pilates POD</span>
            </div>
            <div class="tile-credit-block">
              <Coins :size="28" class="tile-credit-coin" aria-hidden="true" />
              <span class="credit-hero">
                <span class="credit-number">3</span>
                <span class="credit-unit">credits</span>
              </span>
              <span class="credit-detail">per session</span>
              <span class="credit-detail">60 minutes</span>
            </div>
            <router-link to="/pricing#payg" class="tile-secondary-cta">Buy Credits <ArrowRight :size="14" /></router-link>
            <button data-cta="book-pod" @click="this.$router.push('/checkout')" aria-label="Book a Strength, Power or Pilates POD">Book a Strength POD <ArrowRight :size="14" /></button>
          </div>
        </div>
      </div>

      <div id="tiles" v-show="showPricing == 'monthly'">
        <div class="tile monthly">
          <div class="tile-accent"></div>
          <div class="tile-content">
            <div class="tile-header">
              <span class="tile-name">Classic</span>
              <span class="tile-badge">Monthly</span>
            </div>
            <div class="tile-price-block">
              <span class="price-amount">&pound;159</span>
              <span class="price-period">per month</span>
            </div>
            <div class="tile-credits">
              <Coins :size="16" />
              <span><strong>40 credits</strong> per month</span>
            </div>
            <div class="tile-feature">
              <Check :size="16" />
              <span>Average 3 workouts per week</span>
            </div>
            <button @click="this.$router.push('/checkout')">Buy <ArrowRight :size="14" /></button>
            <p class="disclaimer">*Cancel any time. Credits expire at the end of each renewal period, resetting to 40 each month.</p>
          </div>
        </div>

        <div class="tile monthly">
          <div class="tile-accent accent-highlight"></div>
          <div class="tile-content">
            <div class="tile-header">
              <span class="tile-name">Ultimate</span>
              <div class="tile-header-badges">
                <span class="popular-badge">Most popular</span>
                <span class="tile-badge">Monthly</span>
              </div>
            </div>
            <div class="tile-price-block">
              <span class="price-amount">&pound;179</span>
              <span class="price-period">per month</span>
            </div>
            <div class="tile-credits">
              <Coins :size="16" />
              <span><strong>60 credits</strong> per month</span>
            </div>
            <div class="tile-feature">
              <Check :size="16" />
              <span>Average 5 workouts per week</span>
            </div>
            <button @click="this.$router.push('/checkout')">Buy <ArrowRight :size="14" /></button>
            <p class="disclaimer">*Cancel any time. Credits expire at the end of each renewal period, resetting to 60 each month.</p>
          </div>
        </div>
      </div>

      <router-link to="/pricing" class="router-link">See how credits work <span><ArrowRight :size="14" /></span></router-link>
    </section>

    <hr class="section-divider" />

    <section id="reviews">
      <ReviewsSection
        location="all"
        heading="Reviews"
        intro="Real feedback from people training at GYMPODS."
        :show-stats="false"
      />
    </section>

    <hr class="section-divider" />

    <section id="booking">
      <header class="section-header">
        <span class="section-eyebrow">Booking</span>
        <h2>Booking made simple.</h2>
      </header>
      <p class="section-lead">Booking your private gym POD takes less than a minute. Book and manage sessions online, through the GYMPODS app or our WhatsApp AI system.</p>

      <h3>How it works</h3>
      <div class="steps-grid">
        <div class="step-card">
          <div class="step-info">
            <span class="step-num">1</span>
            <div class="step-text">
              <strong>Choose your POD</strong>
              <span>Pick from Strength, Power, Pilates or Cardio</span>
            </div>
          </div>
          <picture>
            <source type="image/webp" srcset="../assets/step-1.webp" />
            <img loading="lazy" decoding="async" src="../assets/step-1.png" alt="GYMPODS app — select your workout type" width="449" height="918" class="step-phone" @click="openLightbox($event.target.src, $event.target.alt)" />
          </picture>
        </div>
        <div class="step-card">
          <div class="step-info">
            <span class="step-num">2</span>
            <div class="step-text">
              <strong>Select your time</strong>
              <span>Book a slot that suits your schedule</span>
            </div>
          </div>
          <picture>
            <source type="image/webp" srcset="../assets/step-2.webp" />
            <img loading="lazy" decoding="async" src="../assets/step-2.png" alt="GYMPODS app — pick your date and time" width="642" height="1320" class="step-phone" @click="openLightbox($event.target.src, $event.target.alt)" />
          </picture>
        </div>
        <div class="step-card">
          <div class="step-info">
            <span class="step-num">3</span>
            <div class="step-text">
              <strong>Book and pay</strong>
              <span>Confirm your session in seconds</span>
            </div>
          </div>
          <picture>
            <source type="image/webp" srcset="../assets/step-3.webp" />
            <img loading="lazy" decoding="async" src="../assets/step-3.png" alt="GYMPODS app — book and pay for your session" width="642" height="1312" class="step-phone" @click="openLightbox($event.target.src, $event.target.alt)" />
          </picture>
        </div>
        <div class="step-card">
          <div class="step-info">
            <span class="step-num">4</span>
            <div class="step-text">
              <strong>You're confirmed</strong>
              <span>Arrive at GYMPODS and train in total privacy</span>
            </div>
          </div>
          <picture>
            <source type="image/webp" srcset="../assets/step-4.webp" />
            <img loading="lazy" decoding="async" src="../assets/step-4.png" alt="GYMPODS app — booking confirmed" width="642" height="1312" class="step-phone" @click="openLightbox($event.target.src, $event.target.alt)" />
          </picture>
        </div>
      </div>

      <div class="app-download">
        <p class="app-label">Book via the GYMPODS app</p>
        <div class="app-buttons">
          <a href="https://apps.apple.com/gb/app/gympods/id6502242401" target="_blank" rel="noopener noreferrer">
            <picture>
              <!--
                Served from public/img/ rather than src/assets/ because
                webpack's vue-loader was silently dropping
                googleplay.webp from the build (appstore.webp was emitted
                fine — same source code, same import path, different
                outcome). Public path bypasses the loader entirely so the
                WebP variant ships reliably for both badges.
              -->
              <source type="image/webp" srcset="/img/appstore.webp" />
              <img loading="lazy" decoding="async" src="/img/appstore.png" alt="Download the GYMPODS app on the App Store" width="480" height="142" />
            </picture>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.gympods.app" target="_blank" rel="noopener noreferrer">
            <picture>
              <source type="image/webp" srcset="/img/googleplay.webp" />
              <img loading="lazy" decoding="async" src="/img/googleplay.png" alt="Get the GYMPODS app on Google Play" width="480" height="143" />
            </picture>
          </a>
        </div>
      </div>

      <div class="whatsapp-support">
        <div class="whatsapp-info">
          <h3>Need Support?</h3>
          <p>Our 24/7 AI-powered assistant is available via WhatsApp to answer questions about availability, pricing, or existing bookings — instantly.</p>
          <a href="https://wa.me/+442080582710" class="whatsapp-btn" target="_blank" rel="noopener noreferrer">
            <IconWhatsApp :size="22" /> Message Us on WhatsApp
          </a>
        </div>
        <div class="whatsapp-image">
          <picture>
            <source type="image/webp" srcset="../assets/ai-image.webp" />
            <img loading="lazy" decoding="async" src="../assets/ai-image.png" alt="GYMPODS WhatsApp AI assistant showing booking interface on a phone" width="1074" height="1068" />
          </picture>
        </div>
      </div>
    </section>

    <hr class="section-divider" />

    <section id="faqs">
      <header class="section-header">
        <span class="section-eyebrow">FAQs</span>
        <h2>Your questions, answered.</h2>
      </header>
      <div class="faqs-top">
        <router-link to="/faqs" class="router-link faqs-top-link">See all FAQs <span><ArrowRight :size="14" /></span></router-link>
      </div>

      <div class="faq-columns">
        <ul class="faq-col" role="list">
          <li
            v-for="faq in faqsCol1"
            :key="faq.id"
            v-memo="[openFAQ === faq.id]"
            class="faq-item"
            :class="{ active: openFAQ === faq.id }"
          >
            <h3>
              <button
                type="button"
                class="faq-question"
                :aria-expanded="openFAQ === faq.id"
                :aria-controls="`faq-panel-${faq.id}`"
                :id="`faq-question-${faq.id}`"
                @click="toggleFAQ(faq.id)"
              >
                <span class="faq-question-text">{{ faq.question }}</span>
                <span class="faq-chevron" aria-hidden="true"><ChevronDown :size="16" /></span>
              </button>
            </h3>
            <div
              :id="`faq-panel-${faq.id}`"
              class="faq-answer"
              role="region"
              :aria-labelledby="`faq-question-${faq.id}`"
              v-show="openFAQ === faq.id"
            >
              <p v-html="faq.answer"></p>
            </div>
          </li>
        </ul>

        <ul class="faq-col" role="list">
          <li
            v-for="faq in faqsCol2"
            :key="faq.id"
            v-memo="[openFAQ === faq.id]"
            class="faq-item"
            :class="{ active: openFAQ === faq.id }"
          >
            <h3>
              <button
                type="button"
                class="faq-question"
                :aria-expanded="openFAQ === faq.id"
                :aria-controls="`faq-panel-${faq.id}`"
                :id="`faq-question-${faq.id}`"
                @click="toggleFAQ(faq.id)"
              >
                <span class="faq-question-text">{{ faq.question }}</span>
                <span class="faq-chevron" aria-hidden="true"><ChevronDown :size="16" /></span>
              </button>
            </h3>
            <div
              :id="`faq-panel-${faq.id}`"
              class="faq-answer"
              role="region"
              :aria-labelledby="`faq-question-${faq.id}`"
              v-show="openFAQ === faq.id"
            >
              <p v-html="faq.answer"></p>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <hr class="section-divider" />

    <section id="final-cta">
      <header class="section-header">
        <span class="section-eyebrow">Get Started</span>
        <h2>Ready to try GYMPODS?</h2>
      </header>
      <p class="section-lead">Book your private gym POD today and experience a smarter way to work out. Start with the Taster Pack: two sessions for just &pound;15, no commitment.<br />Book online, on the app, or via WhatsApp.</p>
      <div class="cta-buttons">
        <router-link to="/checkout" class="cta-button" data-cta="taster-pack">Buy Taster Pack <ArrowRight :size="14" /></router-link>
        <router-link :to="bookPodTo" class="cta-button cta-secondary" data-cta="book-pod">Book a POD <ArrowRight :size="14" /></router-link>
      </div>
      <p class="also-visit">Visit: <router-link to="/putney">GYMPODS Putney</router-link> &nbsp;|&nbsp; <router-link to="/dalston">GYMPODS Dalston</router-link></p>
    </section>
    </main>

    <!-- Equipment Modals -->
    <div class="equipment-modal-overlay" v-if="activeModal" role="dialog" aria-modal="true" aria-label="Equipment details" @click.self="activeModal = null">
      <div class="equipment-modal">
        <div class="modal-header">
          <div class="modal-header-content">
            <span class="modal-pod-label">{{ activeModal === 'cardio' ? 'Cardio PODs' : (activeModal.charAt(0).toUpperCase() + activeModal.slice(1)) + ' POD' }}</span>
            <h3>{{ activeModal === 'cardio' ? 'Choose Your Machine' : 'Full Equipment List' }}</h3>
          </div>
          <button class="modal-close" @click="activeModal = null" aria-label="Close equipment list">&times;</button>
        </div>

        <div class="modal-body" v-if="activeModal === 'strength'">
          <div class="modal-category">
            <h4 class="category-label"><Dumbbell :size="16" /> Bars &amp; Rack</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Move :size="16" /></div><span>Half Rack<small>to 150 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ArrowDownUp :size="16" /></div><span>Dual Assisted Pulley<small>100 kg stacks</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Link :size="16" /></div><span>Pulley Attachments<small>Ropes, V-bars, ankle strap</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Weight :size="16" /></div><span>Olympic Barbells<small>20 kg + 15 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Diamond :size="16" /></div><span>Trap / Hex Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Waves :size="16" /></div><span>EZ Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Menu :size="22" /></div><span>Lat Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Minus :size="16" /></div><span>Dip Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><ArrowUp :size="16" /></div><span>Pull-Up Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Zap :size="16" /></div><span>Landmine Attachment</span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><Weight :size="16" /> Free Weights</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Dumbbell :size="16" /></div><span>Hex Dumbbells<small>1–30 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Weight :size="16" /></div><span>Kettlebells<small>4–24 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Layers :size="16" /></div><span>SBX Rep Set &amp; Bar<small>2.5–37.5 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ShoppingBag :size="16" /></div><span>Core Bags<small>10–25 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><CircleDot :size="16" /></div><span>Medicine Balls<small>to 10 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Volleyball :size="16" /></div><span>Wall Balls + Board<small>to 10 kg</small></span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><Settings :size="16" /> Machines</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Bed :size="16" /></div><span>Incline / Decline Bench<small>Adjustable</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ChevronsUp :size="16" /></div><span>Leg Press<small>to 150 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><RotateCw :size="16" /></div><span>Leg Curl / Extension<small>to 100 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Bike :size="16" /></div><span>HIIT Bike</span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><PersonStanding :size="16" /> Functional &amp; Bodyweight</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Slash :size="16" /></div><span>TRX Suspension Trainer</span></div>
              <div class="modal-tile"><div class="tile-icon"><Circle :size="16" /></div><span>Olympic Rings</span></div>
              <div class="modal-tile"><div class="tile-icon"><Footprints :size="16" /></div><span>Split Squat Stand</span></div>
              <div class="modal-tile"><div class="tile-icon"><Contrast :size="16" /></div><span>BOSU Pro Balance Trainer</span></div>
              <div class="modal-tile"><div class="tile-icon"><Box :size="16" /></div><span>PLYO Box<small>3-sided</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Sofa :size="16" /></div><span>Hip Thrust Box / Pad<small>+ Barbell Pads</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Grid3x3 :size="16" /></div><span>Fitness Deck</span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><Flower :size="16" /> Recovery &amp; Mobility</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Loader2 :size="16" /></div><span>Resistance Bands<small>Glute + mini bands</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ArrowUpDown :size="16" /></div><span>Power Bands<small>Stretch + mini loops</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Circle :size="16" /></div><span>Abs Pad + Abs Wheel</span></div>
              <div class="modal-tile"><div class="tile-icon"><GripHorizontal :size="16" /></div><span>Foam Rollers<small>Incl. deep tissue</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Square :size="16" /></div><span>Stretch Gym Mats</span></div>
              <div class="modal-tile"><div class="tile-icon"><CircleDashed :size="16" /></div><span>Swiss Balls</span></div>
            </div>
          </div>
        </div>

        <div class="modal-body" v-if="activeModal === 'power'">
          <div class="modal-category">
            <h4 class="category-label"><Dumbbell :size="16" /> Machine &amp; Specialist</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Dumbbell :size="16" /></div><span>Smith Machine<small>to 150 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><PersonStanding :size="16" /></div><span>Concept2 SkiErg</span></div>
              <div class="modal-tile"><div class="tile-icon"><Tv :size="16" /></div><span>Echelon Reflect Fitness Mirror<small>On-demand workout classes</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Hand :size="16" /></div><span>Punching Bag + Gloves<small>Freestanding</small></span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><Weight :size="16" /> Bars &amp; Free Weights</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Weight :size="16" /></div><span>Olympic Barbells<small>20 kg + 15 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Diamond :size="16" /></div><span>Trap / Hex Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Waves :size="16" /></div><span>EZ Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Menu :size="22" /></div><span>Lat Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Minus :size="16" /></div><span>Dip Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><ArrowUp :size="16" /></div><span>Pull-Up Bar</span></div>
              <div class="modal-tile"><div class="tile-icon"><Zap :size="16" /></div><span>Landmine Attachment</span></div>
              <div class="modal-tile"><div class="tile-icon"><Dumbbell :size="16" /></div><span>Hex Dumbbells<small>1–30 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Weight :size="16" /></div><span>Kettlebells<small>4–24 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Layers :size="16" /></div><span>SBX Rep Set &amp; Bar<small>2.5–37.5 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ShoppingBag :size="16" /></div><span>Core Bags<small>10–25 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><CircleDot :size="16" /></div><span>Medicine Balls<small>to 10 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Volleyball :size="16" /></div><span>Wall Balls + Board<small>to 10 kg</small></span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><Settings :size="16" /> Machines</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Bed :size="16" /></div><span>Incline / Decline Bench<small>Adjustable</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ChevronsUp :size="16" /></div><span>Leg Press<small>to 150 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><RotateCw :size="16" /></div><span>Leg Curl / Extension<small>to 100 kg</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Bike :size="16" /></div><span>HIIT Bike</span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><PersonStanding :size="16" /> Functional &amp; Bodyweight</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Slash :size="16" /></div><span>TRX Suspension Trainer</span></div>
              <div class="modal-tile"><div class="tile-icon"><Circle :size="16" /></div><span>Olympic Rings</span></div>
              <div class="modal-tile"><div class="tile-icon"><Footprints :size="16" /></div><span>Split Squat Stand</span></div>
              <div class="modal-tile"><div class="tile-icon"><Contrast :size="16" /></div><span>BOSU Pro Balance Trainer</span></div>
              <div class="modal-tile"><div class="tile-icon"><Box :size="16" /></div><span>PLYO Box<small>3-sided</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Sofa :size="16" /></div><span>Hip Thrust Box / Pad<small>+ Barbell Pads</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Grid3x3 :size="16" /></div><span>Fitness Deck</span></div>
            </div>
          </div>
          <div class="modal-category">
            <h4 class="category-label"><Flower :size="16" /> Recovery &amp; Mobility</h4>
            <div class="modal-grid">
              <div class="modal-tile"><div class="tile-icon"><Loader2 :size="16" /></div><span>Resistance Bands<small>Glute + mini bands</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><ArrowUpDown :size="16" /></div><span>Power Bands<small>Stretch + mini loops</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Circle :size="16" /></div><span>Abs Pad + Abs Wheel</span></div>
              <div class="modal-tile"><div class="tile-icon"><GripHorizontal :size="16" /></div><span>Foam Rollers<small>Incl. deep tissue</small></span></div>
              <div class="modal-tile"><div class="tile-icon"><Square :size="16" /></div><span>Stretch Gym Mats</span></div>
              <div class="modal-tile"><div class="tile-icon"><CircleDashed :size="16" /></div><span>Swiss Balls</span></div>
            </div>
          </div>
        </div>

        <div class="modal-body" v-if="activeModal === 'pilates'">
          <div class="modal-grid">
            <div class="modal-tile"><div class="tile-icon"><Flower :size="16" /></div><span>Balanced Body Allegro 2 Reformer</span></div>
            <div class="modal-tile"><div class="tile-icon"><Tv :size="16" /></div><span>On-Screen Pilates Classes<small>Balanced Body subscription</small></span></div>
            <div class="modal-tile"><div class="tile-icon"><Monitor :size="16" /></div><span>32" Touchscreen Display</span></div>
            <div class="modal-tile"><div class="tile-icon"><Thermometer :size="16" /></div><span>Independent Temperature Control</span></div>
            <div class="modal-tile"><div class="tile-icon"><Volume2 :size="16" /></div><span>SONOS Bluetooth Speaker</span></div>
            <div class="modal-tile"><div class="tile-icon"><Square :size="16" /></div><span>Stretch Gym Mats</span></div>
            <div class="modal-tile"><div class="tile-icon"><GripHorizontal :size="16" /></div><span>Foam Rollers</span></div>
          </div>
        </div>

        <div class="modal-body" v-if="activeModal === 'cardio'">
          <p class="cardio-intro">Each Cardio POD features one dedicated machine. Choose from three options when you book:</p>
          <div class="cardio-modal-list">
            <div class="cardio-modal-item">
              <div class="cardio-icon"><Bike :size="16" /></div>
              <div>
                <strong>Peloton Bike+</strong>
                <p>Full Peloton subscription included — access thousands of on-demand and live classes, from cycling to meditation.</p>
              </div>
            </div>
            <div class="cardio-modal-item">
              <div class="cardio-icon"><PersonStanding :size="16" /></div>
              <div>
                <strong>Echelon Stride S Treadmill</strong>
                <p>Full Echelon subscription included — on-demand and live running classes with guided coaching.</p>
              </div>
            </div>
            <div class="cardio-modal-item">
              <div class="cardio-icon"><TrendingUp :size="16" /></div>
              <div>
                <strong>StairMaster</strong>
                <p>The gold standard for lower-body cardio and endurance training.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox overlay -->
    <transition name="lightbox-fade">
      <div v-if="lightboxSrc" class="lightbox-overlay" role="dialog" aria-modal="true" :aria-label="lightboxAlt" @click="closeLightbox">
        <button class="lightbox-close" @click.stop="closeLightbox" aria-label="Close image preview">&times;</button>
        <div class="lightbox-content" @click.stop>
          <img :src="lightboxSrc" :alt="lightboxAlt" class="lightbox-image" />
          <p class="lightbox-caption">{{ lightboxAlt }}</p>
        </div>
      </div>
    </transition>

    <PageFooter />
  </div>
</template>

<script>
import TasterPack from '@/components/TasterPack.vue';
import AsSeenIn from '@/components/AsSeenIn.vue';
import PageFooter from '@/components/PageFooter.vue';
import ReviewsSection from '@/components/ReviewsSection.vue';
import FreePtPromo from '@/components/FreePtPromo.vue';
import { REVIEWS } from '@/data/reviews.js';
import { applySeoHead, removeSeoHead } from '@/utils/seoHead';
import { bookPodRoute } from '@/utils/joinRoute';

// NOTE: these sub-components are imported synchronously. We previously
// async-loaded them via defineAsyncComponent to trim the route bundle,
// but pairing that with the createSSRApp hydration in main.js produced
// a 1.0+ CLS regression: the prerendered HTML contained their fully-
// rendered DOM (~600 px footer + ~1500 px reviews + ~100 px press
// logos), and on client mount Vue hydrated empty placeholders while
// the async chunks loaded — the page collapsed upward by ~2.2 kpx,
// then expanded back when the chunks arrived. Two huge shifts.
// Synchronous imports include them in the route chunk so Vue can
// hydrate in-place with no gap. The TBT cost is small (these
// components have no heavy mount work), well below the CLS penalty.
// Hero video sources — imported (not <source src=…>) so JS can swap
// to the right one on mount based on viewport. See the comment in the
// template above the <video> element for why we don't ship <source>
// children.
import desktopHeroMp4 from '@/assets/gympods-3.mp4';
import { ArrowDownUp, ArrowRight, ArrowUp, ArrowUpDown, Bed, Bike, Box, Check, ChevronDown, ChevronsUp, Circle, CircleDashed, CircleDot, Coins, Contrast, Diamond, Dumbbell, Flower, Footprints, Grid3x3, GripHorizontal, Hand, Layers, Link, Loader2, Maximize2, Menu, Minus, Monitor, Move, Pause, PersonStanding, Play, RotateCw, Settings, Shield, ShoppingBag, Slash, Sofa, SprayCan, Square, Star, Thermometer, TrendingUp, Tv, Volleyball, Volume2, Waves, Weight, Zap } from 'lucide-vue-next';
import IconApple from '@/components/icons/IconApple.vue';
import IconGooglePlay from '@/components/icons/IconGooglePlay.vue';
import IconWhatsApp from '@/components/icons/IconWhatsApp.vue';
import mobileHeroMp4 from '@/assets/gympods-mobile-3.mp4';

const HERO_LOCATION_NAMES = { dalston: 'Dalston', putney: 'Putney' };

// Homepage FAQs — single source of truth for template + JSON-LD schema.
// IDs are stable slugs so aria-controls / aria-labelledby stay readable.
const FAQS = [
  {
    id: 'taster-pack',
    question: 'How do I get the Taster Pack?',
    answer: 'This is available to all new GYMPODS users (exc PT\u2019s), one per person. After registration, either select Taster Pack from your account or when you first book, choose the Taster Pack option when you move to payment options.',
  },
  {
    id: 'credits-expire',
    question: 'How long until my credits expire?',
    answer: 'All unused package credits will expire 6 months after the purchase date. All monthly plan credits expire at the end of each renewal period, so unused credits don\u2019t roll-over into the next month.',
  },
  {
    id: 'cancellation',
    question: 'What\u2019s the POD booking cancellation policy?',
    answer: 'All PODs must be cancelled at least 12 hours before the booking time or you will lose the credits. If you cancel correctly, the credits are added back to your account automatically.',
  },
  {
    id: 'gym-anxiety',
    question: 'Can GYMPODS help with gym anxiety?',
    answer: 'Absolutely. Every session is in your own private POD, so there\u2019s no shared floor, no one watching, and no pressure. If gym anxiety or intimidation has held you back, GYMPODS removes all of that.',
  },
  {
    id: 'train-with-friend',
    question: 'Can I train with a friend at GYMPODS?',
    answer: 'Yes. PODs are spacious enough to train solo or with a partner. Bring a buddy for 1 credit, or pay \u00a37.00 at reception. Bring them for free every Wednesday (Friends-day).',
  },
  {
    id: 'pt-use',
    question: 'Can personal trainers use GYMPODS?',
    answer: 'Absolutely. Many PTs use GYMPODS as their private training base. No competing for equipment, a controlled setting, and a premium environment for client sessions.',
  },
  {
    id: 'payg',
    question: 'Is GYMPODS a pay as you go gym?',
    answer: 'Yes. GYMPODS is a no contract gym. Buy credits and book when you want. If you\u2019d prefer a monthly plan, those are cancel-anytime too. No lock-in, no commitment.',
  },
  {
    id: 'reformer-pilates',
    question: 'Does GYMPODS offer reformer Pilates?',
    answer: 'Yes. Our Pilates POD at GYMPODS Putney is a private reformer studio with a Balanced Body Allegro 2 reformer. It’s the only reformer Pilates gym in Putney where you get the entire room to yourself. Train independently, or find a reformer Pilates instructor via our <a href="/find-a-pro">Find a Pro</a> page.',
  },
];

// ---------------------------------------------------------------------------
// Homepage structured data (JSON-LD).
//
// Two graphs injected at mount:
//   1. Organization — the legal brand entity (sameAs, contactPoint, etc.).
//      The `location` array references Dalston/Putney HealthClub @ids; the
//      full HealthClub schemas live on the location pages themselves.
//   2. WebSite — the site, publisher → Organization.
//
// Deliberately NOT included: HealthClub blocks (Google prefers them on the
// page that actually represents each location — see DalstonView/PutneyView
// + src/data/healthClubLd.js), FAQPage (Google removed rich-result
// eligibility for non-authority sites in Aug 2023; it lives on /faqs where
// it genuinely belongs) and AggregateRating (Google's guidelines forbid
// aggregating third-party review scores).
// ---------------------------------------------------------------------------

const ORG_ID = 'https://gympods.com/#organization';
const WEBSITE_ID = 'https://gympods.com/#website';
const DALSTON_ID = 'https://gympods.com/dalston#healthclub';
const PUTNEY_ID = 'https://gympods.com/putney#healthclub';

const ORG_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'GYMPODS',
  legalName: 'The FIT POD Company Ltd',
  url: 'https://gympods.com/',
  logo: {
    '@type': 'ImageObject',
    url: 'https://gympods.com/assets/logowide.png',
    width: 1080,
    height: 381,
  },
  sameAs: [
    'https://instagram.com/gympods/',
    'https://www.facebook.com/gympods',
    'https://www.tiktok.com/@gympods',
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'info@gympods.com',
      telephone: '+442080582710',
      areaServed: 'GB',
      availableLanguage: ['en-GB'],
    },
  ],
  location: [
    { '@id': DALSTON_ID },
    { '@id': PUTNEY_ID },
  ],
  areaServed: {
    '@type': 'City',
    name: 'London',
    containedInPlace: { '@type': 'Country', name: 'United Kingdom' },
  },
};

const WEBSITE_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: 'https://gympods.com/',
  name: 'GYMPODS',
  inLanguage: 'en-GB',
  publisher: { '@id': ORG_ID },
};

// Ordered injection — Organization first so parsers see the canonical entity
// before the WebSite block references it via publisher @id.
const HOME_JSON_LD = [ORG_LD, WEBSITE_LD];

export default {
  name: 'HomeView',
  components: { AsSeenIn, FreePtPromo,
    TasterPack,
    PageFooter,
    ReviewsSection, ArrowDownUp, ArrowRight, ArrowUp, ArrowUpDown, Bed, Bike, Box, Check, ChevronDown, ChevronsUp, Circle, CircleDashed, CircleDot, Coins, Contrast, Diamond, Dumbbell, Flower, Footprints, Grid3x3, GripHorizontal, Hand, Layers, Link, Loader2, Maximize2, Menu, Minus, Monitor, Move, Pause, PersonStanding, Play, RotateCw, Settings, Shield, ShoppingBag, Slash, Sofa, SprayCan, Square, Star, Thermometer, TrendingUp, Tv, Volleyball, Volume2, Waves, Weight, Zap, IconApple, IconGooglePlay, IconWhatsApp },
  data() {
    return {
      showPricing: 'payg',
      openFAQ: null,
      faqs: FAQS,
      activeModal: null,
      lightboxSrc: null,
      lightboxAlt: '',
      // Mirrors the hero <video> via its play/pause events. Starts true
      // because nothing plays until mounted() sets the src after load.
      heroVideoPaused: true,
    }
  },
  computed: {
    // Same destination as the nav's "Book a POD" (utils/joinRoute.js).
    bookPodTo: bookPodRoute,
    heroStats() {
      return ['dalston', 'putney'].map((key) => {
        const loc = REVIEWS[key];
        return {
          key,
          name: HERO_LOCATION_NAMES[key],
          platforms: [
            {
              name: 'Google',
              rating: loc.averageRating.toFixed(1),
              countLabel: `${loc.googleCountLabel || loc.reviewCount} reviews`,
              url: loc.placeUrl,
            },
            ...(loc.platforms || []).map((p) => ({
              name: p.name,
              // Force one-decimal format so partner ratings read consistently
              // alongside the Google rating above (5 → 5.0, 4.95 → 5.0, 4.9 → 4.9).
              rating: Number(p.rating).toFixed(1),
              countLabel: p.countLabel,
              url: p.url,
            })),
          ],
        };
      });
    },
    // Split FAQs into two balanced columns for the desktop layout.
    // Stacks back into one column at mobile breakpoint via CSS.
    faqsCol1() {
      const mid = Math.ceil(this.faqs.length / 2);
      return this.faqs.slice(0, mid);
    },
    faqsCol2() {
      const mid = Math.ceil(this.faqs.length / 2);
      return this.faqs.slice(mid);
    },
  },
  watch: {
    activeModal(val) {
      // Defer the body-overflow write to the next frame so it doesn't
      // sit inside the click handler's main task — writing to a layout-
      // affecting body style mid-handler invalidates layout right
      // before Vue inserts the modal, forcing a synchronous reflow
      // that adds to INP.
      this._setBodyOverflow(val ? 'hidden' : '');
    },
  },
  methods: {
    // heroVideoPaused is synced from the <video>'s play/pause events,
    // so this only drives the element.
    toggleHeroVideo() {
      const v = this.$refs.heroVideo;
      if (!v) return;
      if (v.paused) {
        if (!v.src && this._loadHeroVideo) this._loadHeroVideo();
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } else {
        v.pause();
      }
    },
    // Helper used by openLightbox/closeLightbox + activeModal watcher.
    // requestAnimationFrame moves the layout-invalidating write off
    // the click handler's task into the next frame; the visual lock
    // happens within ~16 ms, imperceptible to the user, but the click
    // returns control to the browser sooner — better INP.
    _setBodyOverflow(value) {
      if (typeof window === 'undefined') return;
      window.requestAnimationFrame(() => {
        document.body.style.overflow = value;
      });
    },
    toggleFAQ(id) {
      this.openFAQ = this.openFAQ === id ? null : id;
    },
    openLightbox(src, alt) {
      this.lightboxSrc = src;
      this.lightboxAlt = alt;
      this._setBodyOverflow('hidden');
    },
    closeLightbox() {
      this.lightboxSrc = null;
      this.lightboxAlt = '';
      this._setBodyOverflow('');
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        if (this.activeModal) {
          this.activeModal = null;
        } else if (this.lightboxSrc) {
          this.closeLightbox();
        }
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);

    // Pick the hero video variant for THIS viewport and set src — only
    // one mp4 ever downloads. Skipped during the Puppeteer prerender
    // pass (window.__PRERENDER__ flag set in scripts/prerender.js); we
    // want the saved HTML to ship without any src so real client loads
    // pick their own variant fresh, no hydration mismatch.
    //
    // The mobile mp4 is ~3.4 MB. Setting v.src immediately on mount
    // kicks off that download in parallel with app.js and any tag JS
    // still on the wire, so on Slow 4G it can contend for bandwidth
    // inside the LCP window (the mobile hero poster is the LCP
    // element). Poster paints instantly regardless; we defer the mp4
    // fetch until after `load` + one idle tick so it can't compete
    // with LCP for bandwidth. The loop starts ~1–2 s later — fine for
    // a background hero. Guarded by _unmounted (set in unmounted())
    // so the callback is a no-op if the user has navigated away.
    if (typeof window !== 'undefined' && !window.__PRERENDER__) {
      const loadHeroVideo = () => {
        if (this._unmounted) return;
        const v = this.$refs.heroVideo;
        if (!v) return;
        // Reduced-motion visitors keep the static poster; the hero
        // play button calls _loadHeroVideo if they opt in.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this._loadHeroVideo();
      };
      this._loadHeroVideo = () => {
        const v = this.$refs.heroVideo;
        if (!v || v.src) return;
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        v.src = isMobile ? mobileHeroMp4 : desktopHeroMp4;
        v.load();
      };
      const scheduleHeroVideo = () => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(loadHeroVideo, { timeout: 1500 });
        } else {
          setTimeout(loadHeroVideo, 1000);
        }
      };
      if (document.readyState === 'complete') {
        scheduleHeroVideo();
      } else {
        window.addEventListener('load', scheduleHeroVideo, { once: true });
      }
    }

    // applySeoHead removes ~10 existing head tags and inserts ~12 new
    // ones (title, description, og:*, twitter:*, canonical, JSON-LD).
    // On 4× CPU mobile that's ~50–100 ms of main-thread DOM work that
    // user-facing perf doesn't need synchronously — the meta tags
    // aren't visible. During prerender we DO need them synchronous so
    // the saved HTML ships with the per-route metadata for crawlers;
    // the __PRERENDER__ flag handles that branch.
    const applySeo = () => {
      this._seoElements = applySeoHead({
        path: '/',
        title: 'Private Gym PODs in London | GYMPODS',
        description: 'Private gym PODs in Dalston & Putney. No crowds, no waiting, no contract. Train solo, with a buddy or as a PT. Book online or via the GYMPODS app.',
        ogDescription: 'Your own private gym POD. No waiting, no crowds, no gym-timidation. Pay as you go or monthly — cancel anytime.',
        ogImage: '/img/og-home.jpg',
        ogImageAlt: 'GYMPODS — private gym PODs in Dalston and Putney',
        twitterDescription: 'Your own private gym POD. No waiting, no crowds, no gym-timidation.',
        jsonLd: HOME_JSON_LD,
      });
    };
    if (typeof window !== 'undefined' && window.__PRERENDER__) {
      applySeo();
    } else if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(applySeo, { timeout: 1000 });
    } else {
      setTimeout(applySeo, 50);
    }

    // Scroll-triggered fade-in animations.
    // Selector mirrors PutneyView so the homepage feels as smooth: it
    // covers section headers (which are wrapped in <header class="section-header">
    // and therefore don't match the `section > h2` selector on their own),
    // plus all the major repeating cards, grids and banners. Keeping the
    // coverage wide is half of what makes the Putney transitions feel cohesive —
    // if some sibling elements animate and others pop in immediately, the eye
    // registers the inconsistency as "jank".
    // Defer the reveal-animation setup off the critical path. The
    // querySelectorAll + classList.add + IntersectionObserver.observe
    // sequence touches ~30 elements synchronously and was the biggest
    // contributor to the main-thread Style & Layout cost on Lighthouse
    // mobile (~2.75 s total). Pushing it past the next idle moment
    // means TBT only counts the truly critical mount work — the
    // animations themselves still fire on scroll, just with their
    // setup deferred a tick.
    const setupRevealAnimations = () => {
      if (this._unmounted) return;
      const animatedEls = this.$el.querySelectorAll([
        'section:not(#header) > .section-header',
        'section:not(#header) > h2',
        'section:not(#header) > p',
        'section:not(#header) > h3',
        'section:not(#header) > ul',
        'section:not(#header) > .cta-button',
        '.as-seen-in',
        '.why-compare',
        '.why-tile',
        '.location-card',
        '.pod-card',
        '.photo-card',
        '.icon',
        '.faq-item',
        '#selector',
        '.section-divider',
        '.step-card',
        '.whatsapp-support',
        '.app-download',
      ].join(', '));
      animatedEls.forEach(el => el.classList.add('reveal'));

      this._scrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            this._scrollObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px 120px 0px' });

      animatedEls.forEach(el => this._scrollObserver.observe(el));
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(setupRevealAnimations, { timeout: 2000 });
    } else {
      setTimeout(setupRevealAnimations, 100);
    }
  },
  unmounted() {
    // Guards the deferred reveal-setup callback against running after
    // the user has navigated away.
    this._unmounted = true;
    removeSeoHead(this._seoElements);
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
    }
    window.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  },
}
</script>

<style lang="scss" scoped>
$aqua: #7fc0c3;
$charcoal: #231F20;
$carbon: #58595B;
$zest: #D8F789;
$blush: #F9A874;
$midnight: #193446;

// Screen-reader-only utility — visually hidden but exposed to assistive
// tech and crawlers. Used to add keyword-led H1 text to the hero without
// changing the visual design.
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// ---------------------------------------------------------------
// Motion tokens — one source of truth for timing + easing so hover
// and scroll behaviours feel cohesive across the whole page.
// ---------------------------------------------------------------
$ease-out: cubic-bezier(0.23, 1, 0.32, 1);     // Smooth settle — entrances + polished transforms
$ease-std: cubic-bezier(0.4, 0, 0.2, 1);       // Symmetric in-out — state changes
$dur-xs: 0.2s;    // tiny tints / opacity flips
$dur-sm: 0.28s;   // default hover (transforms, colors, shadows)
$dur-md: 0.45s;   // image zoom, larger container moves
$dur-lg: 0.6s;    // scroll reveal + hero entrance

section {
  max-width: 1000px;
  margin: auto;
  text-align: left;

  p {
    margin-bottom: 1.2em;
    color: $carbon;
    line-height: 1.7;
  }
}

h2 {
  font-size: clamp(1.8rem, 3.4vw, 2.4rem);
  font-weight: 800;
  margin: 0 auto;
  color: $midnight;
  display: block;
  line-height: 1.15;
  letter-spacing: -0.02em;
  padding-bottom: 0;
  border-bottom: none;
  max-width: 680px;
}

.section-header {
  text-align: center;
  margin: 0 auto 32px;
  max-width: 720px;

  h2 { margin: 0 auto; }
}

.section-eyebrow {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: $midnight;
  background: rgba($zest, 0.45);
  padding: 7px 16px;
  border-radius: 999px;
  margin-bottom: 18px;
}

.section-lead {
  max-width: 680px;
  margin: 0 auto 32px;
  text-align: center;
  font-size: 1.15rem;
  line-height: 1.7;
  color: $carbon;
}

h3 {
  font-size: 1.15rem;
  font-weight: 600;
  margin-top: 24px;
  margin-bottom: 8px;
  color: $charcoal;
  display: inline-block;
  padding-bottom: 5px;
  border-bottom: 2px solid rgba($aqua, 0.4);
  transition: border-color 0.5s ease, transform 0.5s ease;

  &:hover {
    border-color: darken($aqua, 5%);
    transform: translateY(-1px);
  }
}

.faq-item h3,
.equipment-modal h3,
.tile .tile-name {
  display: inline;
  padding-bottom: 0;
  border-bottom: none;
  margin-top: 0;

  &:hover {
    transform: none;
  }
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: $zest;
  color: $charcoal;
  padding: 15px 30px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  margin: 15px 10px 15px 0;
  transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;

  i, svg {
    margin-left: 4px;
    font-size: 0.85rem;
    display: inline-block;
    transition: transform 0.25s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    opacity: 0.92;

    i { transform: translateX(3px); }
  }

  &:focus-visible {
    outline: 2px solid $charcoal;
    outline-offset: 3px;
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &.cta-secondary {
    // White on $aqua = ~2:1 (fails AA). $charcoal on $aqua = 7.9:1
    // (passes AAA) and reads as a stronger "do this" CTA.
    background-color: $aqua;
    color: $charcoal;

    &:focus-visible { outline-color: $aqua; }
  }
}

.router-link {
  display: inline-block;
  // $aqua on white is ~2:1 — fails AA. Darken to ~5.4:1 while keeping
  // the teal accent. Hover steps slightly darker so the state still
  // reads as "active" and never lighter than rest.
  color: darken($aqua, 30%);
  font-size: 1.2rem;
  text-decoration: none;
  margin-top: 16px;
  transition: color 0.3s ease;

  span {
    margin-left: 2px;
    display: inline-block;
    transition: transform 0.3s ease;
  }

  &:hover {
    color: darken($aqua, 40%);

    span { transform: translateX(5px); }
  }
}

@keyframes tile-offer-pulse {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .tile-offer-pulse {
    animation: none;
    opacity: 0.8;
  }
}

.section-divider {
  max-width: 1000px;
  margin: 56px auto 56px;
  border: none;
  border-top: 1px solid rgba($midnight, 0.08);
}

main > section {
  scroll-margin-top: 80px;
}

// Hero — full-bleed video with the copy set directly on it (no card).
// Legibility comes from the layered scrim in ::after plus a soft
// text-shadow, so the type reads as part of the footage.
#header {
  position: relative;
  // Fills the viewport under the fixed 88 px nav (TasterPack is the
  // spacer). svh keeps it stable while mobile browser chrome animates.
  height: calc(100vh - 88px);
  height: calc(100svh - 88px);
  min-height: 560px;
  max-height: 1080px;
  width: 100%;
  max-width: unset;
  margin: 0;
  display: flex;
  align-items: flex-end;
  background-color: $midnight;
  overflow: hidden;
  isolation: isolate;

  // Hero poster painted here (not on the <video>'s poster attribute)
  // so it can be art-directed per viewport. Desktop default is the
  // landscape 1280×720; the mobile @media override below swaps in
  // the portrait 720×1280. Matching preload hints for both variants
  // are injected in <head> by scripts/prerender.js (route === '/').
  background-image: url('/assets/hero-poster.webp');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
    animation: hero-zoom 24s ease-out forwards;
  }

  // Scrim, bottom to top: a deep wash under the copy, a left-side
  // falloff behind the headline, and a light top shade that softens
  // the seam with the nav.
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background:
      linear-gradient(
        to top,
        rgba($midnight, 0.94) 0%,
        rgba($midnight, 0.74) 25%,
        rgba($midnight, 0.4) 52%,
        rgba($midnight, 0) 78%
      ),
      linear-gradient(
        to right,
        rgba($midnight, 0.62) 0%,
        rgba($midnight, 0.26) 45%,
        rgba($midnight, 0) 72%
      ),
      linear-gradient(
        to bottom,
        rgba($midnight, 0.35) 0%,
        rgba($midnight, 0) 22%
      );
  }

  .header-overlay {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    // Horizontal padding matches the nav so the copy lines up with the logo.
    padding: 0 max(24px, 5%) clamp(56px, 9vh, 104px);
    color: #fff;

    > * { max-width: 820px; }

    .hero-label {
      display: inline-flex;
      align-items: center;
      gap: 14px;
      font-size: 0.78rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.22em;
      color: rgba(255, 255, 255, 0.86);
      margin-bottom: 22px;

      .hero-label-rule {
        width: 36px;
        height: 1px;
        background: $zest;
        flex-shrink: 0;
      }

      .reg-mark {
        font-size: 0.6em;
        vertical-align: super;
        letter-spacing: 0;
        margin-left: 1px;
      }
    }

    h1 {
      margin: 0 0 24px;
      text-shadow: 0 2px 24px rgba($midnight, 0.35);

      // Flex column so the lines' negative margins don't collapse into
      // each other — each line's mask padding is cancelled exactly and
      // the line spacing stays at line-height 0.98.
      .h1-main {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .h1-line {
        display: block;
        font-size: clamp(3rem, 6.6vw, 6.25rem);
        font-weight: 700;
        line-height: 0.98;
        letter-spacing: -0.035em;
        // Mask for the slide-up entrance. At line-height 0.98 Manrope's
        // glyphs overhang the line box, so the mask gets extra room above
        // (ascenders) and below (the "g" descender); the matching negative
        // margins keep the layout unchanged.
        overflow: hidden;
        padding: 0.14em 0 0.3em;
        margin: -0.14em 0 -0.3em;

        > span { display: inline-block; }
      }

      .h1-line--accent {
        color: $zest;
        font-weight: 300;
        letter-spacing: -0.04em;
      }
    }

    .hero-tagline {
      font-size: clamp(1.05rem, 1.35vw, 1.25rem);
      line-height: 1.6;
      margin: 0 0 34px;
      max-width: 520px;
      color: rgba(255, 255, 255, 0.88);
      text-shadow: 0 1px 12px rgba($midnight, 0.45);
    }

    .hero-bottom {
      display: flex;
      align-items: center;
      gap: 28px;
      flex-wrap: wrap;

      .cta-button {
        margin: 0;
        flex-shrink: 0;
        padding: 16px 30px;
        border-radius: 999px;
        box-shadow: 0 10px 30px -10px rgba($zest, 0.55);

        &:hover {
          opacity: 1;
          box-shadow: 0 14px 34px -10px rgba($zest, 0.7);
        }

        &:focus-visible { outline-color: #fff; }
      }

      .hero-apps {
        display: inline-flex;
        align-items: center;
        gap: 24px;
        padding-left: 28px;
        border-left: 1px solid rgba(255, 255, 255, 0.22);
      }

      .hero-app-btn {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        // Full-strength white so the .hero-app-btn-eyebrow doesn't
        // composite below AA on bright video frames.
        color: #fff;
        text-decoration: none;
        transition: transform $dur-xs ease, color $dur-xs ease;

        svg {
          width: 1.5rem;
          height: 1.5rem;
          flex-shrink: 0;
          color: #fff;
        }

        .hero-app-btn-text {
          display: inline-flex;
          flex-direction: column;
          line-height: 1.05;
          text-align: left;
        }

        .hero-app-btn-eyebrow {
          font-size: 0.66rem;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          opacity: 0.72;
          margin-bottom: 3px;
        }

        .hero-app-btn-name {
          font-size: 0.98rem;
          font-weight: 600;
          letter-spacing: 0.01em;
        }

        &:hover,
        &:focus-visible {
          color: #fff;
          transform: translateY(-2px);

          .hero-app-btn-eyebrow { opacity: 1; }
        }

        &:focus-visible {
          outline: 2px solid rgba(255, 255, 255, 0.7);
          outline-offset: 4px;
          border-radius: 4px;
        }
      }
    }
  }

  // Bottom-right: video pause/play + scroll cue.
  .hero-controls {
    position: absolute;
    right: max(24px, 5%);
    bottom: clamp(56px, 9vh, 104px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
  }

  .hero-video-toggle {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba($midnight, 0.25);
    color: #fff;
    cursor: pointer;
    transition: background $dur-xs ease, border-color $dur-xs ease;

    &:hover {
      background: rgba(255, 255, 255, 0.14);
      border-color: rgba(255, 255, 255, 0.6);
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 3px;
    }
  }

  .hero-scroll {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: rgba(255, 255, 255, 0.75);
    text-decoration: none;

    .hero-scroll-text {
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.24em;
      text-transform: uppercase;
      writing-mode: vertical-rl;
    }

    .hero-scroll-line {
      position: relative;
      width: 1px;
      height: 56px;
      background: rgba(255, 255, 255, 0.25);
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 40%;
        background: $zest;
      }
    }

    &:hover { color: #fff; }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 6px;
      border-radius: 4px;
    }
  }

  // Staggered entrance — desktop only, for the same LCP reason the
  // old card entrance was desktop-only (see .reveal notes below).
  @media (min-width: 769px) and (prefers-reduced-motion: no-preference) {
    .hero-label {
      animation: hero-fade-up 0.8s $ease-out 0.1s both;
    }

    .h1-line > span {
      animation: hero-line-up 1s $ease-out both;
    }
    .h1-line:nth-child(1) > span { animation-delay: 0.18s; }
    .h1-line:nth-child(2) > span { animation-delay: 0.3s; }

    .hero-tagline {
      animation: hero-fade-up 0.8s $ease-out 0.5s both;
    }

    .hero-bottom {
      animation: hero-fade-up 0.8s $ease-out 0.62s both;
    }

    .hero-controls {
      animation: hero-fade-in 1s ease 0.9s both;
    }

    .hero-scroll-line::after {
      animation: hero-scroll-cue 2.4s $ease-std 1.6s infinite;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    video { animation: none; }
  }
}

// Locations
#locations {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 5% 40px;
}

.locations-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.home-free-pt-promo {
  margin-top: 24px;
}

@media (min-width: 769px) {
  .location-card--putney { order: 1; }
  .location-card--dalston { order: 2; }
}

.location-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 30px 36px 26px;
  background: #fff;
  border: 1px solid rgba($midnight, 0.08);
  border-radius: 22px;
  box-shadow: 0 14px 40px -20px rgba($midnight, 0.25);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 30px 50px -22px rgba($midnight, 0.35);
  }
}

.location-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
}

.location-new-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: $zest;
  color: $midnight;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  border-radius: 999px;
  box-shadow: 0 4px 14px -4px rgba($zest, 0.9);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $midnight;
    animation: newPulse 1.8s ease-in-out infinite;
  }
}

@keyframes newPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.6); opacity: 0.5; }
}

.location-card--dalston .location-accent {
  background: linear-gradient(90deg, $aqua 0%, lighten($aqua, 18%) 100%);
}

.location-card--putney .location-accent {
  background: linear-gradient(90deg, $zest 0%, lighten($zest, 5%) 100%);
}

.location-card-head {
  margin-bottom: 20px;
  padding-top: 2px;
}

.location-prefix {
  display: block;
  font-size: 0.8rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: $carbon;
  font-weight: 700;
  margin-bottom: 6px;
}

.location-city {
  margin: 0;
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: 800;
  color: $midnight;
  letter-spacing: -0.03em;
  line-height: 1;
}

.location-stats {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;

  li {
    display: flex;
  }
}

.location-stat-row {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 16px 12px;
  text-align: center;
  text-decoration: none;
  color: inherit;
  transition: background 0.18s ease, transform 0.18s ease;

  &--link {
    cursor: pointer;
  }

  &--link:hover,
  &--link:focus-visible {
    background: rgba($zest, 0.14);

    .location-platform {
      color: $midnight;
    }
  }

  &--link:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: -2px;
  }
}

.location-card--dalston .location-stat-row--link:hover,
.location-card--dalston .location-stat-row--link:focus-visible {
  background: rgba($aqua, 0.12);
}

.location-platform {
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 700;
  color: $carbon;
}

.location-rating {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 2rem;
  font-weight: 800;
  color: $midnight;
  letter-spacing: -0.03em;
  line-height: 1;

  i, svg {
    color: $zest;
    font-size: 1.1rem;
    fill: currentColor;
    stroke: color-mix(in srgb, currentColor 55%, #000);
    stroke-width: 1.5;
    filter: drop-shadow(0 1px 2px rgba($midnight, 0.2));
  }

  strong { font-weight: inherit; }
}

.location-card--dalston .location-rating i { color: $aqua; }

.location-count {
  font-size: 0.82rem;
  color: $carbon;
  font-weight: 500;
  white-space: nowrap;
}

.location-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  border-radius: 999px;
  background: $midnight;
  color: #fff;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;
  margin-top: auto;

  i, svg {
    font-size: 0.88rem;
    transition: transform 0.22s ease;
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    i { transform: translateX(3px); }
  }

  &:focus-visible {
    outline: 2px solid $aqua;
    outline-offset: 3px;
  }
}

.location-card--dalston .location-cta:hover,
.location-card--dalston .location-cta:focus-visible {
  background: $aqua;
  color: $midnight;
}

.location-card--putney .location-cta:hover,
.location-card--putney .location-cta:focus-visible {
  background: $zest;
  color: $midnight;
}

@media (max-width: 768px) {
  #locations {
    // Trimmed top padding from 48 → 24 px to close the gap below the
    // hero. The hero overlay's own bottom padding (32 px) already
    // gives a breathing line; #locations only needs enough top
    // padding to introduce the eyebrow as a new section, not to
    // re-establish hero-level vertical rhythm.
    padding: 24px 5% 24px;
  }

  .locations-header {
    margin-bottom: 28px;
  }

  .connector { display: none; }

  .map-eyebrow { font-size: 22px; }
  .marker-label { font-size: 18px; }
  .thames-label { font-size: 14px; }

  .locations-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .location-card {
    padding: 28px 24px 24px;
  }

  .location-card-head {
    margin-bottom: 22px;
  }

  .location-stats li {
    padding: 16px 6px;
  }

  .location-rating {
    font-size: 1.7rem;
    i { font-size: 0.95rem; }
  }

  .location-count {
    font-size: 0.72rem;
  }

  .location-cta {
    padding: 14px 20px;
    font-size: 0.95rem;
  }
}

// Intro
#intro {
  text-align: center;

  .intro-lead {
    max-width: 1000px;
  }

  .intro-secondary {
    margin-top: 48px;
    font-weight: 500;
    color: $charcoal;
    font-size: 1.05rem;
    max-width: 680px;
    margin-left: auto;
    margin-right: auto;
  }

  #features {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin: 48px auto 0;
    max-width: 1100px;

    .why-tile {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 18px;
      padding: 24px 22px 22px;
      background: #fff;
      border: 1px solid rgba($carbon, 0.12);
      border-radius: 18px;
      text-align: left;
      overflow: hidden;
      transition: transform 0.3s cubic-bezier(0.23, 1, 0.32, 1),
                  border-color 0.3s ease,
                  box-shadow 0.3s ease;

      &:hover,
      &:focus-within {
        transform: translateY(-2px);
        border-color: rgba($aqua, 0.45);
        box-shadow: 0 12px 28px -18px rgba($midnight, 0.28);

        .feature-circle {
          transform: scale(1.06);
          box-shadow: 0 4px 14px rgba($zest, 0.45);
        }

        .why-tile-proof {
          transform: translateY(0);
        }
      }

      &:focus-visible {
        outline: 2px solid $aqua;
        outline-offset: 3px;
      }

      .feature-circle {
        width: 52px;
        height: 52px;
        border-radius: 50%;
        background: $zest;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        i { font-size: 1.2rem; color: $charcoal; }
      }

      .why-tile-body {
        width: 100%;
      }

      h3 {
        font-size: 0.98rem;
        font-weight: 700;
        color: $charcoal;
        letter-spacing: 0.01em;
        margin: 0 0 8px;
        line-height: 1.25;
      }

      .why-tile-desc {
        font-size: 0.98rem;
        font-weight: 500;
        color: $charcoal;
        line-height: 1.5;
        margin: 0 0 10px;
      }

      .why-tile-proof {
        font-size: 0.9rem;
        font-weight: 400;
        // Was opacity:0.7 at rest which dropped contrast below AA on
        // white. The hover animation kept the translateY slide so the
        // "settles into place" feel survives without the fade.
        color: $carbon;
        line-height: 1.55;
        margin: 0;
        transform: translateY(2px);
        transition: transform 0.35s ease;
      }
    }
  }

  .why-compare {
    max-width: 780px;
    margin: 56px auto 0;
    padding: 28px 32px 30px;
    text-align: center;
    border-top: 1px solid rgba($aqua, 0.45);

    .why-compare-eyebrow {
      font-size: 0.86rem;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      // Was $aqua (#7fc0c3) on white — ~2:1 contrast, Lighthouse fail.
      // Darken keeps the teal accent while clearing AA (5.4:1).
      color: darken($aqua, 30%);
      margin: 0 0 16px;
    }

    .why-compare-body {
      font-size: 1rem;
      font-weight: 500;
      color: $charcoal;
      line-height: 1.7;
      margin: 0 auto;
      max-width: 680px;
    }
  }
}

// Tighten the gap between the GYMPODS Difference block and the divider that follows
#intro + .section-divider {
  margin-top: 20px;
}

// Tighten the divider between Reviews and Booking — the Reviews marquee ends with a
// dense, bordered card row while Booking opens with a light eyebrow + h2, so the
// standard divider rhythm reads as a void here. Asymmetric: less above (cards already
// feel resolved by their border), slightly more below so the Booking eyebrow pill
// isn't pinned to the rule.
#reviews + .section-divider {
  margin-top: 40px;
  margin-bottom: 48px;
}

// Train how you like
#train-how {
  text-align: center;

  #icons.photo-cards {
    display: flex;
    justify-content: center;
    gap: 28px;
    margin-top: 40px;

    .photo-card {
      flex: 1;
      max-width: 310px;
      position: relative;
      overflow: hidden;
      border-radius: 20px;
      aspect-ratio: 1 / 1;
      cursor: default;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      transition: transform $dur-sm $ease-out,
                  box-shadow $dur-sm $ease-out;

      &:nth-child(1) .photo-card-img img { object-position: center 90%; }
      &:nth-child(2) .photo-card-img img { object-position: center 78%; }
      &:nth-child(3) .photo-card-img img { object-position: center 40%; }

      &:hover {
        transform: translateY(-6px);
        box-shadow: 0 16px 40px rgba($midnight, 0.22);

        .photo-card-img img {
          transform: scale(1.04);
        }

        .photo-card-accent {
          width: 95%;
        }
      }
    }

    .photo-card-img {
      position: absolute;
      inset: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center 50%;
        display: block;
        transition: transform $dur-md $ease-out;
      }
    }

    .photo-card-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: 28px 24px;
      background: linear-gradient(to top, rgba($midnight, 0.85) 0%, rgba($midnight, 0.2) 45%, transparent 65%);
    }

    .photo-card-accent {
      display: block;
      width: 85%;
      height: 2px;
      // Card 1 stays Aqua (set here as the default). The two
      // :nth-child overrides below rotate the second and third cards
      // through Blush and Zest — this is the page's most controlled
      // showcase of the full active brand palette (Aqua + Blush +
      // Zest), without any single colour dominating.
      background: rgba($aqua, 0.7);
      margin-bottom: 14px;
      transition: width 0.4s ease;
    }

    .photo-card:nth-child(2) .photo-card-accent {
      background: rgba($blush, 0.8);
    }

    .photo-card:nth-child(3) .photo-card-accent {
      background: rgba($zest, 0.85);
    }

    .photo-card-title {
      font-size: 1rem;
      font-weight: 600;
      color: #fff;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      margin: 0 0 6px 0;
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    }

    .photo-card-sub {
      font-size: 0.82rem;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.78);
      margin: 0;
      line-height: 1.45;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }
}

// Pods
#pods {
  text-align: center;
}

// Pod Cards
.pod-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: auto;
  gap: 20px;
  margin-top: 28px;
  text-align: left;
}

// POD cards — one flat midnight surface: title, tick list, equipment
// row and a pill button, with no nested panels.
.pod-card {
  background: $midnight;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px -18px rgba($midnight, 0.6);
  }

  .pod-image-wrap {
    position: relative;
    overflow: hidden;
    aspect-ratio: 5 / 4;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.5s ease;
    }
  }

  &:hover .pod-image-wrap img {
    transform: scale(1.04);
  }

  .pod-badge {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba($midnight, 0.72);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #fff;
    font-size: 0.76rem;
    font-weight: 600;
    letter-spacing: 0.01em;

    svg { color: $zest; }
  }

  .pod-body {
    padding: 26px 28px 28px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    flex: 1;
  }

  .pod-info {
    h3 {
      display: block;
      margin: 0 0 6px;
      padding: 0;
      border: 0;
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: -0.02em;
      text-transform: none;
      color: #fff;

      &:hover { transform: none; }
    }

    p {
      margin: 0;
      font-size: 1rem !important;
      line-height: 1.5;
      color: rgba(255, 255, 255, 0.72);
    }
  }

  .perfect-for {
    p {
      margin: 0 0 12px;
      font-size: 0.72rem !important;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: $zest;

      strong { font-weight: inherit; }
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 9px;

      li {
        position: relative;
        padding-left: 24px;
        font-size: 0.95rem;
        line-height: 1.45;
        color: rgba(255, 255, 255, 0.92);

        // Tick drawn from two borders — no icon import needed.
        &::before {
          content: '';
          position: absolute;
          left: 4px;
          top: 0.18em;
          width: 5px;
          height: 10px;
          border: solid $zest;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        span { display: none; }
      }
    }
  }

  // Flex so any number of items shares the row evenly.
  .equipment-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: auto;
    padding-top: 22px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);

    .equip-item {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      text-align: center;

      svg {
        box-sizing: content-box;
        width: 18px;
        height: 18px;
        padding: 11px;
        border-radius: 12px;
        background: rgba($aqua, 0.12);
        color: $aqua;
      }

      span {
        font-size: 0.74rem;
        line-height: 1.3;
        color: rgba(255, 255, 255, 0.78);
      }
    }

    // "See Putney / See Dalston" pair — equipment differs by site, so
    // each tile links to that location's Choose-your-POD section.
    .pod-location-links {
      flex-basis: 100%;
      margin-top: 14px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;

      .pod-location-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 12px 16px;
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: #fff;
        font-size: 0.9rem;
        font-weight: 600;
        text-decoration: none;
        white-space: nowrap;
        transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;

        svg { transition: transform 0.25s ease; }

        &:hover {
          background: $zest;
          border-color: $zest;
          color: $charcoal;

          svg { transform: translateX(2px); }
        }

        &:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
        }
      }
    }
  }

  // Cardio POD: the note introduces the "one of" row, so the divider
  // sits above the note rather than between the two.
  .cardio-note {
    margin: auto 0 -6px;
    padding-top: 22px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 0.9rem !important;
    color: rgba(255, 255, 255, 0.72);
  }

  .cardio-options.equipment-strip {
    display: grid;
    grid-template-columns: 1fr auto 1fr auto 1fr;
    margin-top: 0;
    padding-top: 0;
    border-top: 0;

    .pod-location-links { grid-column: 1 / -1; }

    .equip-or {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
      align-self: start;
      margin-top: 12px;
    }
  }
}

// Equipment Modals
.equipment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  overscroll-behavior: contain;

  @media (prefers-reduced-motion: no-preference) {
    animation: fadeIn 0.2s ease;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes hero-zoom {
  from { transform: scale(1.08); }
  to { transform: scale(1); }
}

@keyframes hero-fade-up {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes hero-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes hero-line-up {
  // Starts far enough down to sit below the enlarged mask.
  from { transform: translateY(145%); }
  to { transform: translateY(0); }
}

@keyframes hero-scroll-cue {
  0% { transform: translateY(-100%); }
  60%, 100% { transform: translateY(250%); }
}

.equipment-modal {
  background: darken($midnight, 3%);
  border-radius: 18px;
  max-width: 640px;
  width: 100%;
  max-height: 85vh;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba($aqua, 0.15);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba($aqua, 0.08);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (prefers-reduced-motion: no-preference) {
    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 28px 20px;
    background: linear-gradient(135deg, $midnight 0%, darken($midnight, 2%) 100%);
    border-bottom: 1px solid rgba($aqua, 0.12);
    flex-shrink: 0;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 28px;
      right: 28px;
      height: 2px;
      background: linear-gradient(90deg, $aqua, $zest);
      border-radius: 2px;
    }
  }

  .modal-header-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
  }

  .modal-pod-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: $aqua;
  }

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 500;
    color: #fff;
    letter-spacing: 0.01em;
  }

  .modal-close {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.3rem;
    cursor: pointer;
    line-height: 1;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.2);
    }
  }

  .modal-body {
    padding: 24px 28px 28px;
    overflow-y: auto;
    flex: 1;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: rgba($aqua, 0.2); border-radius: 4px; }
  }

  .modal-category {
    &:not(:first-child) {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
    }
  }

  .category-label {
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba($aqua, 0.7);
    margin: 0 0 14px;
    display: flex;
    align-items: center;
    gap: 8px;

    i { font-size: 0.72rem; color: rgba($aqua, 0.5); }
  }

  .modal-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .modal-tile {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    min-height: 54px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba($aqua, 0.06);
      border-color: rgba($aqua, 0.15);
    }

    .tile-icon {
      width: 34px;
      height: 34px;
      min-width: 34px;
      border-radius: 8px;
      background: rgba($aqua, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;

      i { font-size: 0.85rem; color: $aqua; }
    }

    span {
      font-size: 0.72rem;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.3;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;

      small {
        font-size: 0.62rem;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.4);
        margin-top: 1px;
        text-align: left;
      }
    }
  }

  .cardio-intro {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0 0 20px;
    text-align: left;
  }

  .cardio-modal-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .cardio-modal-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 12px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba($aqua, 0.05);
      border-color: rgba($aqua, 0.12);
    }

    .cardio-icon {
      width: 44px;
      height: 44px;
      min-width: 44px;
      border-radius: 10px;
      background: rgba($aqua, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;

      i { font-size: 1.1rem; color: $aqua; }
    }

    > div:last-child { text-align: left; }

    strong {
      color: #fff;
      font-size: 0.9rem;
      font-weight: 600;
      display: block;
      margin-bottom: 4px;
      text-align: left;
    }

    p {
      color: rgba(255, 255, 255, 0.55);
      font-size: 0.78rem;
      line-height: 1.5;
      margin: 0;
      text-align: left;
    }
  }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

// Pricing
#pricing {
  text-align: center;

  .section-lead a {
    color: inherit;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-decoration-color: rgba($charcoal, 0.35);
    text-underline-offset: 3px;
    transition: text-decoration-color 0.2s ease;

    &:hover,
    &:focus-visible {
      text-decoration-color: $aqua;
    }
  }

  #selector {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 32px;

    button {
      background-color: rgba(#D9D9D9, 0.6);
      font-size: 1.05rem;
      padding: 1rem 2rem;
      border: none;
      border-radius: 50px;
      cursor: pointer;
      font-weight: 500;
      color: $charcoal;
      letter-spacing: 0.02em;
      transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }

      &:active {
        transform: translateY(0);
        box-shadow: none;
      }

      &.active {
        background-color: $zest;
        font-weight: 600;
      }
    }
  }

  .pricing-fade-enter-active { transition: opacity 0.8s ease-in-out; }
  .pricing-fade-leave-active { transition: opacity 0.4s ease-out; }
  .pricing-fade-enter-from { opacity: 0; }
  .pricing-fade-leave-to { opacity: 0; }

  #tiles {
    display: flex;
    gap: 20px;
    margin-top: 24px;
  }

  .tile {
    flex: 1;
    background-color: $midnight;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    transition: transform 0.4s ease, box-shadow 0.4s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    }

    .tile-accent {
      height: 4px;
      flex-shrink: 0;
      background: linear-gradient(90deg, $aqua, lighten($aqua, 10%));
    }

    .tile-content {
      padding: 30px 26px;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 400px;
    }

    .tile-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      gap: 10px;

      .tile-name {
        font-size: 0.95rem;
        font-weight: 600;
        color: white;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.15em;
        line-height: 1.5;
      }

      .tile-header-badges {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 10px;
        flex-shrink: 0;
      }
    }

    .tile-duration,
    .tile-badge {
      font-size: 0.8rem;
      font-weight: 500;
      color: rgba(white, 0.7);
      background: rgba(white, 0.08);
      padding: 5px 12px;
      border-radius: 20px;
      white-space: nowrap;

      i { margin-right: 4px; font-size: 0.7rem; }
    }

    .tile-price-block {
      text-align: center;
      padding: 20px 0;
      margin-bottom: 16px;
      border-bottom: 1px solid rgba(white, 0.08);
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .price-amount {
        display: block;
        font-size: 3.5rem;
        font-weight: 800;
        color: $zest;
        line-height: 1.1;
        letter-spacing: -0.02em;
      }

      .price-period {
        display: block;
        font-size: 0.9rem;
        color: rgba(white, 0.6);
        margin-top: 6px;
      }
    }

    .tile-credits {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      background: rgba($aqua, 0.1);
      border-radius: 10px;
      margin-bottom: 12px;
      color: $aqua;

      i, svg { color: $aqua; font-size: 0.9rem; flex-shrink: 0; }
      span { color: $aqua; font-size: 0.95rem; }
      strong { color: $aqua; }
    }

    .tile-credit-block {
      text-align: center;
      padding: 24px 0 20px;
      margin-bottom: 16px;
      border-bottom: 1px solid rgba(white, 0.08);
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;

      .tile-credit-coin {
        color: $zest;
        margin-bottom: 6px;
      }

      .credit-hero {
        display: inline-flex;
        align-items: baseline;
        justify-content: center;
        gap: 10px;
        margin-bottom: 4px;
      }

      .credit-number {
        font-size: 4.4rem;
        font-weight: 800;
        color: $zest;
        line-height: 1;
        letter-spacing: -0.03em;
      }

      .credit-unit {
        font-size: 1.15rem;
        font-weight: 700;
        color: white;
        text-transform: uppercase;
        letter-spacing: 0.12em;
      }

      .credit-detail {
        display: block;
        font-size: 0.88rem;
        color: rgba(white, 0.6);
        line-height: 1.35;

        &:first-of-type { margin-top: 4px; }
      }
    }

    .tile-taster-offer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 14px;
      margin-bottom: 12px;
      background: linear-gradient(135deg, rgba($aqua, 0.12) 0%, rgba($zest, 0.08) 100%);
      border: 1px solid rgba($aqua, 0.28);
      border-radius: 10px;
      text-decoration: none;
      transition: border-color 0.3s ease, background 0.3s ease, transform 0.25s ease;

      &:hover {
        border-color: rgba($aqua, 0.55);
        background: linear-gradient(135deg, rgba($aqua, 0.18) 0%, rgba($zest, 0.12) 100%);

        .tile-taster-cta i { transform: translateX(3px); }
      }

      &:focus-visible {
        outline: 2px solid $aqua;
        outline-offset: 2px;
      }
    }

    .tile-taster-meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
      text-align: left;
      min-width: 0;
    }

    .tile-taster-eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 0.62rem;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: $aqua;
    }

    .tile-offer-pulse {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: $aqua;
      animation: tile-offer-pulse 2.6s ease-in-out infinite;
    }

    .tile-taster-deal {
      font-size: 0.92rem;
      font-weight: 600;
      color: white;
      letter-spacing: -0.005em;
    }

    .tile-taster-price {
      color: $zest;
      font-weight: 800;
    }

    .tile-taster-cta {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: $aqua;
      white-space: nowrap;
      flex-shrink: 0;

      i, svg {
        font-size: 0.65rem;
        transition: transform 0.25s ease;
      }
    }

    .tile-feature {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 16px;
      margin-bottom: 12px;

      i, svg { color: $aqua; font-size: 0.8rem; flex-shrink: 0; }
      span { color: rgba(white, 0.85); font-size: 0.9rem; }
    }

    button {
      background-color: $zest;
      color: $charcoal;
      font-family: inherit;
      font-size: 1rem;
      font-weight: 600;
      padding: 14px 24px;
      margin-top: auto;
      border: none;
      border-radius: 10px;
      width: 100%;
      cursor: pointer;
      letter-spacing: 0.02em;
      transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;

      i, svg {
        margin-left: 4px;
        font-size: 0.85rem;
        display: inline-block;
        transition: transform 0.25s ease;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
        opacity: 0.92;

        i { transform: translateX(3px); }
      }

      &:active {
        transform: translateY(0);
        box-shadow: none;
      }
    }

    .tile-secondary-cta {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      background: transparent;
      color: white;
      border: 1px solid rgba(white, 0.22);
      border-radius: 10px;
      padding: 12px 20px;
      font-size: 0.95rem;
      font-weight: 600;
      text-decoration: none;
      letter-spacing: 0.02em;
      margin-bottom: 10px;
      transition: border-color 0.3s ease, background 0.3s ease, color 0.3s ease, transform 0.25s ease;

      svg {
        transition: transform 0.25s ease;
      }

      &:hover {
        border-color: $aqua;
        color: $aqua;
        background: rgba($aqua, 0.08);

        svg {
          transform: translateX(3px);
        }
      }

      &:focus-visible {
        outline: 2px solid $aqua;
        outline-offset: 2px;
      }
    }

    .disclaimer {
      font-size: 0.72rem;
      color: rgba(white, 0.4);
      margin-top: 14px;
      margin-bottom: 0;
      line-height: 1.4;
    }

    .popular-badge {
      background: $zest;
      color: $charcoal;
      font-size: 0.68rem;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 20px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      white-space: nowrap;
    }

    &.monthly {
      .tile-accent.accent-highlight {
        background: linear-gradient(90deg, $zest, lighten($zest, 5%));
      }
    }
  }
}

// Reviews
#reviews {
  min-height: 200px;

  p { font-size: 1.2rem; }
}

// Booking
#booking {
  text-align: center;

  h3 {
    display: block;
    margin: 48px auto 20px;
    text-align: center;
    border-bottom: none;
    padding-bottom: 0;
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: $midnight;
  }

  .steps-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 20px;
  }

  .step-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 18px 22px 22px;
    background: rgba($midnight, 0.04);
    border: 1px solid rgba($aqua, 0.12);
    border-radius: 14px;
    overflow: hidden;
    transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-3px);
      border-color: rgba($aqua, 0.3);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }
  }

  .step-info {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex: 1;
    min-width: 0;
  }

  .step-phone {
    width: 100px;
    height: auto;
    flex-shrink: 0;
    border-radius: 10px;
    box-shadow: 0 3px 14px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
    }
  }

  .step-num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    background-color: $aqua;
    color: white;
    font-weight: 700;
    font-size: 0.85rem;
    transition: transform 0.4s ease, background-color 0.4s ease;
  }

  .step-card:hover .step-num {
    transform: scale(1.06);
    background-color: darken($aqua, 8%);
  }

  .step-text {
    display: flex;
    flex-direction: column;
    gap: 3px;

    strong { font-size: 1rem; font-weight: 600; color: $charcoal; }
    span { font-size: 0.85rem; color: $carbon; line-height: 1.4; }
  }

  .app-download {
    margin-top: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;

    .app-label {
      font-size: 1rem;
      font-weight: 600;
      color: $carbon;
      margin: 0;
      white-space: nowrap;
    }
  }

  .app-buttons {
    display: flex;
    align-items: center;
    gap: 12px;

    a {
      display: flex;
      align-items: center;
      transition: transform 0.3s ease, opacity 0.3s ease;

      &:hover { transform: translateY(-2px); opacity: 0.85; }
      &:active { transform: translateY(0); }
      img { height: 44px; width: auto; display: block; }
    }
  }

  .whatsapp-support {
    display: flex;
    align-items: stretch;
    gap: 0;
    margin-top: 28px;
    background: linear-gradient(135deg, $midnight 0%, darken($midnight, 4%) 100%);
    border: 1px solid rgba($aqua, 0.15);
    border-radius: 14px;
    overflow: hidden;
    transition: transform 0.3s ease, border-color 0.3s ease;

    &:hover {
      transform: translateY(-3px);
      border-color: rgba($aqua, 0.35);
    }
  }

  .whatsapp-info {
    flex: 1;
    padding: 36px 36px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    h3 { color: #fff; margin-top: 0; margin-bottom: 15px; }
    p { font-size: 1.15rem; line-height: 1.7; color: rgba(255, 255, 255, 0.85); }
  }

  .whatsapp-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 18px;
    padding: 14px 28px;
    // White on #25D366 (WhatsApp Light Green) = ~1.75:1, AA fail.
    // #075E54 is the official WhatsApp "Teal Dark" (chat-header brand
    // colour) and gives white text 7:1 contrast.
    background: #075E54;
    color: #fff;
    font-size: 1.15rem;
    font-weight: 600;
    border-radius: 8px;
    text-decoration: none;
    transition: background 0.2s ease, transform 0.15s ease;
    align-self: center;

    i { font-size: 1.5rem; }
    &:hover { background: #0a8474; transform: translateY(-1px); }
  }

  .whatsapp-image {
    flex: 0 0 420px;

    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
}

// FAQs
#faqs {
  .faqs-top {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;

    .faqs-top-link {
      margin-top: 0;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }

  .faq-columns {
    display: flex;
    gap: 16px;
  }

  .faq-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .faq-item {
    margin-bottom: 0;
    padding: 0;
    background: rgba($midnight, 0.03);
    border: 1px solid rgba($aqua, 0.1);
    border-radius: 12px;
    transition: background-color 0.25s ease, border-color 0.25s ease;
    overflow: hidden;

    h3 {
      margin: 0;
      display: block;
    }

    &:hover {
      background: rgba($midnight, 0.06);
      border-color: rgba($aqua, 0.22);
    }

    &.active {
      background: rgba($midnight, 0.06);
      border-color: rgba($aqua, 0.3);

      .faq-chevron {
        background: rgba($aqua, 0.2);
        i { transform: rotate(180deg); color: $aqua; }
      }

      .faq-question-text { color: $aqua; }
    }

    // The <button> trigger — full-width so the whole header row is clickable,
    // inherits button reset so it looks like the old div-based header.
    .faq-question {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 16px 18px;
      min-height: 60px;
      background: none;
      border: 0;
      text-align: left;
      font: inherit;
      cursor: pointer;
      color: inherit;

      &:focus-visible {
        outline: 2px solid $aqua;
        outline-offset: -2px;
        border-radius: 12px;
      }
    }

    .faq-question-text {
      flex: 1;
      font-size: 0.95rem;
      font-weight: 600;
      color: $charcoal;
      line-height: 1.4;
      transition: color 0.25s ease;
    }

    .faq-chevron {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      min-width: 30px;
      border-radius: 50%;
      background: rgba($aqua, 0.08);
      transition: background-color 0.25s ease;

      i, svg {
        font-size: 0.7rem;
        color: $carbon;
        transition: transform 0.3s ease, color 0.25s ease;
      }
    }

    &:hover .faq-chevron { background: rgba($aqua, 0.16); }

    .faq-answer {
      padding: 0 18px 16px;

      p {
        font-size: 0.95rem;
        line-height: 1.6;
        margin: 0;
        color: $carbon;
        border-top: 1px solid rgba($aqua, 0.08);
        padding-top: 12px;

        a {
          color: $aqua;
          text-decoration: none;
          font-weight: 500;

          &:hover { text-decoration: underline; }
        }
      }
    }
  }
}

// Final CTA
#final-cta {
  text-align: center;
  padding: 56px 24px;
  background: none;
  border-radius: 0;
  margin-top: 16px;

  h2 { text-align: center; }

  p {
    font-size: 1.2rem;
    max-width: 700px;
    margin: 0 auto 20px;
  }

  .cta-buttons {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin: 28px 0;
  }

  .also-visit {
    font-size: 1rem;
    color: $carbon;

    a {
      // $aqua on white = ~2:1 (AA fail). Same darkened-aqua treatment
      // as .router-link so the visited-locations cluster reads as
      // links without sacrificing contrast.
      color: darken($aqua, 30%);
      text-decoration: none;
      font-weight: 600;
      transition: color 0.2s ease;

      &:hover { color: darken($aqua, 40%); }
    }
  }
}

// ---------------------------------------------------------------
// Scroll reveal
// Matches PutneyView's timing exactly so both pages feel identical.
// Three deliberate choices make the difference between "smooth" and "janky":
//   1. Plain `ease-out` (cubic-bezier(0, 0, 0.58, 1)) — decelerates naturally.
//      A steeper settle like `cubic-bezier(0.23, 1, 0.32, 1)` crawls in the
//      final 20% and reads as elements "hanging" mid-reveal on a long page.
//   2. No `will-change`. Applying it to every .reveal on a homepage this
//      long promotes dozens of composite layers simultaneously and can
//      actually stutter on mid-range devices.
//   3. No stagger delays. The staggered grid entries (up to 420ms) meant
//      slower items were still animating when the user had already scrolled
//      past them — popping in behind the viewport boundary. A single-phase
//      reveal per element is crisper on a page this dense.
// ---------------------------------------------------------------
// Scroll-reveal — optimised for smooth scroll:
//  - translate3d forces a GPU compositor layer (no on-demand promotion jank)
//  - will-change primes the layer before the animation, then drops to auto
//    on .revealed so the layer is released and doesn't bloat memory.
//  - 400ms ease-out matches how quickly users scroll past content.
.reveal {
  opacity: 0;
  transform: translate3d(0, 14px, 0);
  transition: opacity 0.4s ease-out, transform 0.4s ease-out;
  will-change: opacity, transform;
}

.reveal.revealed {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  will-change: auto;
}

/* Disable .reveal fade on mobile (and for reduced-motion users on any
   viewport). The fade was causing multi-second LCP render delays on
   simulated 4× mobile CPU because Lighthouse times the settled paint
   after the 0.4s opacity transition completes. Desktop CPU is fast
   enough that the fade-in completes inside the LCP window, so the
   premium scroll-reveal effect stays. */
@media (prefers-reduced-motion: reduce), (max-width: 768px) {
  .reveal,
  .reveal.revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

// Responsive
@media screen and (max-width: 768px) {
  section {
    padding: 0 20px;
    box-sizing: border-box;
  }

  .section-lead {
    font-size: 1.02rem;
  }

  .section-header h2 {
    max-width: 100%;
  }

  #header {
    padding: 0;
    // Mobile nav is 68 px. Capped so tall phones still show the copy
    // without a sea of video above it.
    height: calc(100vh - 68px);
    height: calc(100svh - 68px);
    min-height: 520px;
    max-height: 780px;
    // Portrait poster for mobile — the LCP element on this viewport.
    // Overrides the desktop landscape default set in the base block.
    background-image: url('/assets/hero-poster-mobile.webp');

    // JS in mounted() sets the <video src> to gympods-mobile-3.mp4
    // (3.3 MB) on this viewport. hero-zoom (the desktop ken-burns) is
    // off: continuous transform on a full-viewport video is heavy on
    // mobile GPUs and reads as fidgety on a small screen.
    video {
      display: block;
      animation: none;
    }

    // Copy is stacked at the bottom, so the scrim leans on the bottom
    // half and keeps a light wash overall for bright frames.
    &::after {
      background:
        linear-gradient(
          to top,
          rgba($midnight, 0.96) 0%,
          rgba($midnight, 0.8) 35%,
          rgba($midnight, 0.35) 65%,
          rgba($midnight, 0.2) 100%
        );
    }

    .header-overlay {
      padding: 0 22px 32px;

      .hero-label {
        font-size: 0.68rem;
        letter-spacing: 0.18em;
        gap: 10px;
        margin-bottom: 16px;

        .hero-label-rule { width: 24px; }
      }

      h1 {
        margin-bottom: 16px;

        .h1-line { font-size: clamp(2.3rem, 10.4vw, 3.4rem); }
      }

      .hero-tagline { font-size: 1rem; margin-bottom: 24px; }

      .hero-bottom {
        gap: 18px;

        .cta-button { padding: 14px 26px; font-size: 1rem; }

        .hero-apps {
          gap: 18px;
          padding-left: 0;
          border-left: none;
        }

        .hero-app-btn .hero-app-btn-name { font-size: 0.9rem; }
      }
    }

    // Only the pause control on mobile, tucked into the top corner so
    // it never crowds the CTAs.
    .hero-controls {
      top: 16px;
      right: 16px;
      bottom: auto;
    }

    .hero-video-toggle {
      width: 36px;
      height: 36px;
    }

    .hero-scroll { display: none; }
  }

  #intro {
    #features {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      max-width: 100%;
      margin-top: 32px;

      .why-tile {
        padding: 18px 16px;
        gap: 14px;
        border-radius: 14px;

        .feature-circle {
          width: 44px;
          height: 44px;
          i { font-size: 1rem; }
        }

        h3 {
          font-size: 0.9rem;
        }

        .why-tile-desc {
          font-size: 0.9rem;
          margin-bottom: 8px;
        }

        .why-tile-proof {
          // Was opacity: 0.8 — composited to ~3.7:1 on white, AA fail.
          // Drop opacity here so the mobile rule doesn't re-mute the
          // text we just fixed at the desktop default.
          transform: none;
          font-size: 0.84rem;
        }
      }
    }

    .why-compare {
      margin-top: 40px;
      padding: 22px 18px 24px;

      .why-compare-body {
        font-size: 0.92rem;
      }
    }
  }

  #train-how {
    #icons.photo-cards {
      flex-direction: column;
      gap: 16px;
      margin-top: 28px;

      .photo-card {
        max-width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 16px;

        &:nth-child(1) .photo-card-img img { object-position: center 90%; }
        &:nth-child(2) .photo-card-img img { object-position: center 78%; }
        &:nth-child(3) .photo-card-img img { object-position: center 40%; }
      }

      .photo-card-overlay {
        padding: 22px 20px;
      }

      .photo-card-accent {
        width: 85%;
        margin-bottom: 10px;
      }

      .photo-card-title {
        font-size: 0.88rem;
      }

      .photo-card-sub {
        font-size: 0.76rem;
      }
    }
  }

  .pod-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .pod-card {
    .pod-body {
      padding: 22px 20px 24px;
      gap: 18px;
    }

    .pod-info h3 {
      font-size: 1.4rem;
    }

    .equipment-strip {
      gap: 4px;
      padding-top: 18px;

      .equip-item {
        gap: 6px;

        svg { padding: 9px; }

        span { font-size: 0.66rem; }
      }

      .pod-location-links .pod-location-link {
        padding: 11px 10px;
        font-size: 0.85rem;
      }
    }

    .cardio-note { padding-top: 18px; }

    .cardio-options.equipment-strip { padding-top: 0; }
  }

  .equipment-modal {
    max-height: calc(100vh - 32px);
    border-radius: 14px;

    .modal-header {
      padding: 20px 20px 16px;
      &::after { left: 20px; right: 20px; }
    }

    .modal-body { padding: 20px 20px 80px; }

    .modal-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }

    .modal-tile {
      padding: 8px 10px;

      .tile-icon {
        width: 30px;
        height: 30px;
        min-width: 30px;
        border-radius: 7px;
        i { font-size: 0.75rem; }
      }

      span {
        font-size: 0.65rem;
        small { font-size: 0.57rem; }
      }
    }
  }

  #pricing {
    #selector {
      width: 100%;
      justify-content: center;

      button { font-size: 0.95rem; padding: 0.9rem 1.6rem; }
    }

    #tiles {
      flex-direction: column;
      gap: 16px;

      .tile {
        &:hover { transform: none; box-shadow: none; }
        .tile-content { padding: 24px 20px; min-height: auto; }
        .tile-header .tile-name { font-size: 0.85rem; }
        .tile-price-block .price-amount { font-size: 2.8rem; }
        .tile-credit-block {
          padding: 18px 0 16px;

          .credit-number { font-size: 3.5rem; }
          .credit-unit { font-size: 1rem; }
          .credit-detail { font-size: 0.82rem; }
        }
      }
    }
  }

  #booking {
    .steps-grid { grid-template-columns: 1fr; gap: 12px; }
    .step-card { padding: 14px 12px 14px 16px; }
    .step-phone { width: 80px; }
    .app-download { flex-direction: column; align-items: flex-start; gap: 12px; }
    .whatsapp-support { flex-direction: column; }
    .whatsapp-info { padding: 25px 20px; }

    .whatsapp-image {
      flex: 0 0 auto;
      img { height: 280px; object-fit: cover; object-position: top center; }
    }

    .whatsapp-btn {
      width: 100%;
      justify-content: center;
      align-self: stretch;
      box-sizing: border-box;
      font-size: 0.95rem;
      padding: 14px 16px;
    }
  }

  #faqs {
    .faqs-top {
      gap: 8px;
      margin-bottom: 16px;

      .faqs-top-link { margin-top: 0; }
    }

    .faq-columns { flex-direction: column; gap: 8px; }

    .faq-item .faq-question {
      padding: 14px 14px;
      .faq-question-text { font-size: 0.88rem; }
    }

    .faq-item .faq-answer { padding: 0 14px 14px; }
  }

  #final-cta {
    .cta-buttons {
      flex-direction: column;
      align-items: center;
      // Tighten the vertical stack on mobile. Each .cta-button has
      // its own margin: 15px 0 baseline (for stacking elsewhere on
      // the page) — inside this flex column we want gap alone to
      // own spacing, otherwise the visible gap was 15+gap+15 ≈ 38 px.
      gap: 12px;
      .cta-button { margin: 0; }
    }
  }
}

@media screen and (max-width: 360px) {
  .equipment-modal .modal-grid {
    grid-template-columns: 1fr !important;
  }
}

// Lightbox
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.lightbox-close {
  position: absolute;
  top: 20px;
  right: 28px;
  background: none;
  border: none;
  color: #fff;
  font-size: 2.4rem;
  cursor: pointer;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover { opacity: 1; }
}

.lightbox-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
  max-width: 90vw;
  max-height: 90vh;
}

.lightbox-image {
  max-height: 80vh;
  max-width: 400px;
  width: auto;
  border-radius: 20px;
  box-shadow: 0 12px 50px rgba(0, 0, 0, 0.4);
  animation: lightbox-pop 0.3s ease;
}

.lightbox-caption {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  margin-top: 16px;
  text-align: center;
  letter-spacing: 0.03em;
}

@keyframes lightbox-pop {
  0% { transform: scale(0.85); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.25s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}
</style>
